import { usePaidTraining } from "../training-payments/TrainingAccess";
import { isUnlimitedPuzzleTraining, nextPuzzleShouldShowPaywall } from "../training-payments/puzzleTrainingGate";
import { trackUsageEvent } from "../analytics/UsageAnalytics";
import {
  puzzleTrainingRequest,
  trainingErrorMessage,
  guestDailyQuota,
  reserveGuestPuzzle,
  guestTrackResumeIndex,
  currentDailyPuzzleToken,
  nextDailyPuzzleToken,
} from "../training-payments/dailyPuzzleApi";
import React, { useRef, useEffect, useState, useContext, useMemo } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import Chessground from "@react-chess/chessground";
import { Chess } from "chess.js";
import "chessground/assets/chessground.base.css";
import "chessground/assets/chessground.brown.css";
import "chessground/assets/chessground.cburnett.css";
import "./play/mobile-training-look.css";
import CustomAlert from "../components/CustomAlert";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faCircleInfo,
  faLightbulb,
  faRepeat,
  faShareNodes,
} from "@fortawesome/free-solid-svg-icons";

import {
  getPuzzle,
  getFirstPuzzle,
  checkPuzzleResult,
  getPuzzlesByCategory,
  getCategoryInfo,
} from "../integrations/APIClient";
import Promotion from "../components/PromotionComponent";
import WebTrainingAdSlot from "../components/WebTrainingAdSlot";
import ResponsiveAppMenu from "../components/ResponsiveAppMenu";
import { useLanguage } from "../contexts/LanguageContext";
import { usePro } from "../contexts/ProContext";
import { UserContext } from "../contexts/UserContext";
import { useOwnerQa } from "../contexts/OwnerQaContext";
import {
  GUEST_TRAINING_TRACKS,
  getGuestTrainingTrack,
} from "../config/guestTraining";
import {
  buildTrainingHouseQuery,
  findTrainingHouseByRating,
  findTrainingHouseById,
  findTrainingHouseByRange,
  formatTrainingHouseRange,
  backendTrainingHouseId,
  resolveAdaptiveTrainingHouse,
  isTrainingHouseOutgrown,
  isTrainingHouseUndershot,
  normalizeTrainingRating,
} from "../config/trainingMap";
import { toDests, toColor, toPgnHistory, game } from "../utils/chessUtils";

const EMPTY_BOARD_FEN = "8/8/8/8/8/8/8/8";
const DEFAULT_SUCCESS_MESSAGE = "Correct move! Well done!";
const DEFAULT_FAIL_MESSAGE = "Wrong answer. Try again.";
const DEFAULT_HINT_MESSAGE = "Here's a hint for you!";
const FREE_TRAINING_AD_EVERY = 5;
const FREE_TRAINING_AD_CLOSE_DELAY_SECONDS = 15;
const HOUSE_SHIFT_REDIRECT_DELAY_MS = 39000;
const FREE_TRAINING_SOLVE_COUNTER_KEY = "chesshood_free_training_solved_count";
const CATEGORY_LABEL_MAP = {
  anastasiasmate: "Anastasia's Mate",
  anastasiaMate: "Anastasia's Mate",
  backrankmate: "Back Rank Mate",
  backRankMate: "Back Rank Mate",
  basic: "Basic",
  capturingdefender: "Capturing Defender",
  "capturing-defender": "Capturing Defender",
  endgame: "Endgame",
  mate: "Mate",
  mateinfive: "Mate in 5",
  mateInFive: "Mate in 5",
  mateIn1: "Mate in 1",
  mateInOne: "Mate in 1",
  oneMove: "One Move",
  queenbishopmate: "Queen + Bishop Battery",
  queenBishopMate: "Queen + Bishop Battery",
  queenmove: "Queen Move",
  queenMove: "Queen Move",
  short: "Short Tactics",
};

function capitalizeLanguage(language) {
  return typeof language === "string" && language
    ? language.charAt(0).toUpperCase() + language.slice(1)
    : "";
}

function resolveLocalizedField(source, baseKey, language, fallbackValue = "") {
  if (!source || typeof source !== "object") {
    return fallbackValue;
  }

  const suffix = capitalizeLanguage(language);
  const candidates = [
    source[`${baseKey}_${language}`],
    source[`${baseKey}${suffix}`],
    source?.translations?.[baseKey]?.[language],
    source?.translations?.[language]?.[baseKey],
    source?.localized?.[baseKey]?.[language],
    source?.localized?.[language]?.[baseKey],
    source[baseKey],
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }

    if (candidate && typeof candidate === "object") {
      const nested =
        candidate[language] || candidate.en || candidate.pl || candidate.es;
      if (typeof nested === "string" && nested.trim()) {
        return nested.trim();
      }
    }
  }

  return fallbackValue;
}

function readSolvedPuzzleCount() {
  if (typeof window === "undefined") {
    return 0;
  }

  try {
    const rawValue = window.localStorage.getItem(
      FREE_TRAINING_SOLVE_COUNTER_KEY,
    );
    const parsedValue = Number.parseInt(rawValue || "0", 10);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
  } catch (error) {
    console.error("Failed to read free training solve counter:", error);
    return 0;
  }
}

function extractPuzzleId(puzzleLike) {
  if (!puzzleLike || typeof puzzleLike !== "object") return null;

  if (typeof puzzleLike.id === "string" && puzzleLike.id) return puzzleLike.id;
  if (typeof puzzleLike.puzzleId === "string" && puzzleLike.puzzleId) {
    return puzzleLike.puzzleId;
  }
  if (typeof puzzleLike._id === "string" && puzzleLike._id)
    return puzzleLike._id;
  if (puzzleLike._id && typeof puzzleLike._id === "object") {
    if (typeof puzzleLike._id.$oid === "string" && puzzleLike._id.$oid) {
      return puzzleLike._id.$oid;
    }
    if (typeof puzzleLike._id.oid === "string" && puzzleLike._id.oid) {
      return puzzleLike._id.oid;
    }
  }

  return null;
}

function normalizeMoveList(afterPgn) {
  if (Array.isArray(afterPgn)) {
    return afterPgn.filter(Boolean);
  }
  if (typeof afterPgn === "string") {
    return afterPgn.split(/\s+/).filter(Boolean);
  }
  return [];
}

function normalizeMoveNotation(move) {
  if (typeof move !== "string") {
    return "";
  }

  return move
    .trim()
    .toLowerCase()
    .replace(/[+#?!]+$/g, "");
}

function parseCoordinateMove(move) {
  if (typeof move !== "string") {
    return null;
  }

  const match = move
    .trim()
    .toLowerCase()
    .match(/^([a-h][1-8])([a-h][1-8])([qrbn])?$/);

  if (!match) {
    return null;
  }

  return {
    from: match[1],
    to: match[2],
    promotion: match[3],
  };
}

function moveSolvesByCheckmate(move, chess) {
  const parsedMove = parseCoordinateMove(move);

  if (!parsedMove) {
    return false;
  }

  const validationGame = new Chess(chess.fen());
  const playedMove = validationGame.move(parsedMove);

  return Boolean(playedMove && validationGame.isCheckmate());
}

function isPuzzleCandidate(value) {
  return Boolean(
    value &&
    typeof value === "object" &&
    (value.fenPosition || value.fen || value.after_pgn || value.afterPgn),
  );
}

function unwrapPuzzleResponse(payload) {
  if (Array.isArray(payload)) {
    return payload[0] ?? null;
  }

  if (!payload || typeof payload !== "object") {
    return payload;
  }

  const nestedKeys = [
    "puzzle",
    "trainingPuzzle",
    "nextPuzzle",
    "currentPuzzle",
    "data",
    "item",
    "result",
  ];

  for (const key of nestedKeys) {
    if (isPuzzleCandidate(payload[key])) {
      return payload[key];
    }
  }

  if (Array.isArray(payload.puzzles) && payload.puzzles.length) {
    return payload.puzzles[0];
  }

  if (Array.isArray(payload.items) && payload.items.length) {
    return payload.items[0];
  }

  return payload;
}

function normalizePuzzle(payload, language = "en") {
  const rawPuzzle = unwrapPuzzleResponse(payload);

  if (!isPuzzleCandidate(rawPuzzle)) {
    return null;
  }

  const puzzleId = extractPuzzleId(rawPuzzle) || extractPuzzleId(payload);

  return {
    ...rawPuzzle,
    id: rawPuzzle.id || puzzleId || undefined,
    _id: rawPuzzle._id || (puzzleId ? { $oid: puzzleId } : undefined),
    fenPosition: rawPuzzle.fenPosition || rawPuzzle.fen || EMPTY_BOARD_FEN,
    after_pgn: normalizeMoveList(
      rawPuzzle.after_pgn ?? rawPuzzle.afterPgn ?? rawPuzzle.moves,
    ),
    description: resolveLocalizedField(rawPuzzle, "description", language, ""),
    success_message: resolveLocalizedField(
      rawPuzzle,
      "success_message",
      language,
      rawPuzzle.successMessage || rawPuzzle.success || DEFAULT_SUCCESS_MESSAGE,
    ),
    fail_message: resolveLocalizedField(
      rawPuzzle,
      "fail_message",
      language,
      rawPuzzle.failMessage ||
        rawPuzzle.failure_message ||
        rawPuzzle.failureMessage ||
        rawPuzzle.fail ||
        DEFAULT_FAIL_MESSAGE,
    ),
  };
}

function normalizePuzzleCollection(payload, language = "en") {
  if (Array.isArray(payload)) {
    return payload
      .map((item) => normalizePuzzle(item, language))
      .filter(Boolean);
  }

  if (!payload || typeof payload !== "object") {
    return [];
  }

  const listCandidates = [
    payload.puzzles,
    payload.items,
    payload.data,
    payload.results,
    payload.rows,
  ];

  for (const candidate of listCandidates) {
    if (Array.isArray(candidate)) {
      return candidate
        .map((item) => normalizePuzzle(item, language))
        .filter(Boolean);
    }
  }

  const singlePuzzle = normalizePuzzle(payload, language);
  return singlePuzzle ? [singlePuzzle] : [];
}

function resolveFeedbackMessage(
  result,
  currentPuzzle,
  kind,
  language = "en",
  options = {},
) {
  const categoryMessage =
    options && typeof options === "object" ? options.categoryMessage || "" : "";

  if (kind === "success") {
    return (
      resolveLocalizedField(result, "success_message", language) ||
      result?.successMessage ||
      result?.message ||
      currentPuzzle?.success_message ||
      categoryMessage ||
      DEFAULT_SUCCESS_MESSAGE
    );
  }

  if (kind === "fail") {
    return (
      resolveLocalizedField(result, "fail_message", language) ||
      result?.failMessage ||
      result?.message ||
      currentPuzzle?.fail_message ||
      categoryMessage ||
      DEFAULT_FAIL_MESSAGE
    );
  }

  if (kind === "hint") {
    return (
      resolveLocalizedField(result, "hint_message", language) ||
      result?.hintMessage ||
      result?.message ||
      DEFAULT_HINT_MESSAGE
    );
  }

  return DEFAULT_HINT_MESSAGE;
}

function getUserDisplayName(user, firebaseUser) {
  const fullName =
    user?.fullName ||
    user?.full_name ||
    user?.name ||
    firebaseUser?.displayName ||
    "";

  if (typeof fullName === "string" && fullName.trim()) {
    return fullName.trim();
  }

  const username = user?.username || user?.nick || "";
  if (typeof username === "string" && username.trim()) {
    return username.trim();
  }

  const email = user?.email || firebaseUser?.email || "";
  if (typeof email === "string" && email.trim()) {
    return email.split("@")[0];
  }

  return "Chesshood player";
}

function formatCategoryLabel(category) {
  if (!category) {
    return "training";
  }

  return String(category).trim().toLowerCase();
}

function normalizeCategoryKey(category) {
  return String(category || "")
    .trim()
    .toLowerCase();
}

function humanizeCategoryToken(token) {
  if (!token) {
    return "";
  }

  const normalizedLookupKey = normalizeCategoryKey(token);

  if (CATEGORY_LABEL_MAP[token]) {
    return CATEGORY_LABEL_MAP[token];
  }

  if (CATEGORY_LABEL_MAP[normalizedLookupKey]) {
    return CATEGORY_LABEL_MAP[normalizedLookupKey];
  }

  const normalizedToken = String(token)
    .trim()
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

  return normalizedToken.replace(/\b\w/g, (character) =>
    character.toUpperCase(),
  );
}

function getPuzzleCategoryTokens(currentPuzzle, guestTrack) {
  const rawCategorySource =
    currentPuzzle?.categories ||
    currentPuzzle?.category ||
    guestTrack?.backendCategory ||
    guestTrack?.title ||
    "";

  const rawTokens = Array.isArray(rawCategorySource)
    ? rawCategorySource
    : String(rawCategorySource).split(/[\s,|/]+/);

  return [
    ...new Set(
      rawTokens.map((token) => normalizeCategoryKey(token)).filter(Boolean),
    ),
  ];
}

function pickWeakestPuzzleCategoryKey(categoryTokens, categoryRatings) {
  if (!Array.isArray(categoryTokens) || categoryTokens.length === 0) {
    return "";
  }

  const normalizedRatings =
    categoryRatings && typeof categoryRatings === "object"
      ? categoryRatings
      : {};
  let chosenToken = categoryTokens[0];
  let chosenRating = Number.isFinite(Number(normalizedRatings[chosenToken]))
    ? Number(normalizedRatings[chosenToken])
    : 0;

  categoryTokens.forEach((token) => {
    const nextRating = Number.isFinite(Number(normalizedRatings[token]))
      ? Number(normalizedRatings[token])
      : 0;

    if (
      nextRating < chosenRating ||
      (nextRating === chosenRating && token.localeCompare(chosenToken) < 0)
    ) {
      chosenToken = token;
      chosenRating = nextRating;
    }
  });

  return chosenToken;
}

function getPuzzleCategoryLabels(currentPuzzle, guestTrack) {
  const labels = getPuzzleCategoryTokens(currentPuzzle, guestTrack)
    .map((token) => humanizeCategoryToken(token))
    .filter(Boolean);

  return [...new Set(labels)];
}

function getPuzzleDifficultyValue(puzzle) {
  const rawRating = puzzle?.rating ?? puzzle?.puzzleRating ?? puzzle?.elo;
  const numericRating = Number(rawRating);

  return Number.isFinite(numericRating) ? numericRating : Number.MAX_SAFE_INTEGER;
}

function comparePuzzlesByDifficulty(leftPuzzle, rightPuzzle) {
  const difficultyDifference =
    getPuzzleDifficultyValue(leftPuzzle) - getPuzzleDifficultyValue(rightPuzzle);

  if (difficultyDifference !== 0) {
    return difficultyDifference;
  }

  const leftId = String(extractPuzzleId(leftPuzzle) || "");
  const rightId = String(extractPuzzleId(rightPuzzle) || "");

  return leftId.localeCompare(rightId);
}

function getGuestTrackSources(guestTrack) {
  if (!guestTrack) {
    return [];
  }

  if (guestTrack.id === "starter-seven") {
    return GUEST_TRAINING_TRACKS.filter(
      (track) => !track.hidden && track.backendCategory,
    );
  }

  return guestTrack.backendCategory ? [guestTrack] : [];
}

async function loadGuestTrackPuzzles(track, language) {
  const sourceTracks = getGuestTrackSources(track);

  if (!sourceTracks.length) {
    throw new Error("Guest starter category is not configured.");
  }

  const puzzleGroups = await Promise.all(
    sourceTracks.map(async (sourceTrack) => {
      const guestResponse = await getPuzzlesByCategory(
        sourceTrack.backendCategory,
        0,
        0,
        language,
      );

      const normalizedPuzzles = normalizePuzzleCollection(
        guestResponse,
        language,
      ).sort(comparePuzzlesByDifficulty);

      return normalizedPuzzles
        .slice(0, sourceTrack.puzzleCount || normalizedPuzzles.length)
        .map((puzzleItem) => ({
          ...puzzleItem,
          guestTrackId: sourceTrack.id,
          guestTrackTitle: sourceTrack.title,
          guestTrackCategory: sourceTrack.backendCategory,
        }));
    }),
  );

  const combinedPuzzles = puzzleGroups.flat();

  if (!combinedPuzzles.length) {
    throw new Error("No guest starter puzzles are available in this category yet.");
  }

  return combinedPuzzles.slice(0, track?.puzzleCount || combinedPuzzles.length);
}

function getTrainingCopy(currentPuzzle) {
  const description = currentPuzzle?.description;

  if (typeof description === "string" && description.trim()) {
    return description.trim();
  }

  const category = String(currentPuzzle?.category || "").toLowerCase();
  if (category.includes("short")) {
    return "Short tactics puzzles finish in two or three moves.";
  }

  return "Find the best continuation and finish the puzzle cleanly.";
}

function getHintCopy(currentPuzzle, moveIndex) {
  const expectedMove = currentPuzzle?.after_pgn?.[moveIndex];
  const parsedMove = parseCoordinateMove(expectedMove);

  if (!parsedMove) {
    return DEFAULT_HINT_MESSAGE;
  }

  try {
    const hintGame = new Chess(currentPuzzle?.fenPosition || EMPTY_BOARD_FEN);
    const piece = hintGame.get(parsedMove.from);
    const pieceNameMap = {
      p: "pawn",
      n: "knight",
      b: "bishop",
      r: "rook",
      q: "queen",
      k: "king",
    };
    const pieceName = pieceNameMap[piece?.type] || "piece";
    const isMateMove = moveSolvesByCheckmate(expectedMove, hintGame);

    if (isMateMove) {
      return `Look for a mating move with your ${pieceName} from ${parsedMove.from}.`;
    }

    return `Look for a forcing move with your ${pieceName} from ${parsedMove.from} toward ${parsedMove.to}.`;
  } catch (error) {
    return DEFAULT_HINT_MESSAGE;
  }
}

function buildHintPreviewConfig(startFen, moves, step, orientation) {
  const fallbackFen =
    startFen && startFen !== EMPTY_BOARD_FEN
      ? startFen
      : "8/8/8/8/8/8/8/8 w - - 0 1";

  try {
    const previewGame = new Chess(fallbackFen);

    moves.slice(0, step).forEach((move) => {
      const parsedMove = parseCoordinateMove(move);
      if (parsedMove) {
        previewGame.move(parsedMove);
        return;
      }

      previewGame.move(move, { sloppy: true });
    });

    return {
      fen: previewGame.fen(),
      orientation:
        orientation || (fallbackFen.split(" ")[1] === "w" ? "black" : "white"),
      movable: {
        free: false,
        color: "both",
        dests: new Map(),
      },
      draggable: {
        enabled: false,
      },
    };
  } catch (error) {
    return {
      fen: fallbackFen,
      orientation:
        orientation || (fallbackFen.split(" ")[1] === "w" ? "black" : "white"),
      movable: {
        free: false,
        color: "both",
        dests: new Map(),
      },
      draggable: {
        enabled: false,
      },
    };
  }
}

function formatRating(value) {
  if (value === 0) {
    return "0";
  }

  if (value === null || value === undefined || value === "") {
    return "--";
  }

  return String(value);
}

export default function Play({ guestMode = false, guestTrackId = "" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { id: puzzleID } = useParams();
  const puzzle = useRef(null);
  const [puzzleFinished, setPuzzleFinished] = useState(false);
  const moveNr = useRef(0);
  const [config, setConfig] = useState({});
  const [isLoadingPuzzle, setIsLoadingPuzzle] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [alertInfo, setAlertInfo] = useState({
    show: false,
    variant: "",
    message: "",
    success: "",
  });
  const [promoting, setPromoting] = useState(false);
  const [activePanel, setActivePanel] = useState("idle");
  const [hintCopy, setHintCopy] = useState("");
  const [hintStartFen, setHintStartFen] = useState("");
  const [hintMoves, setHintMoves] = useState([]);
  const [hintStep, setHintStep] = useState(0);
  const [solvedPuzzleCount, setSolvedPuzzleCount] = useState(() =>
    readSolvedPuzzleCount(),
  );
  const [requestedPuzzleId, setRequestedPuzzleId] = useState(puzzleID || null);
  const [loadMode, setLoadMode] = useState(puzzleID ? "specific" : "feed");
  const [loadNonce, setLoadNonce] = useState(0);
  const [guestPuzzleIndex, setGuestPuzzleIndex] = useState(() => guestTrackResumeIndex(guestTrackId));
  const [guestUsedToday, setGuestUsedToday] = useState(() => guestDailyQuota().usedToday);
  const [dailyAccess, setDailyAccess] = useState(null);
  const [guestTrackPuzzles, setGuestTrackPuzzles] = useState([]);
  const [shareNotice, setShareNotice] = useState("");
  const [showAdPopup, setShowAdPopup] = useState(false);
  const [openedAdBreakCount, setOpenedAdBreakCount] = useState(null);
  const [adCloseCountdown, setAdCloseCountdown] = useState(0);
  const [trainingAdRenderState, setTrainingAdRenderState] = useState("idle");
  const [pendingTrainingMapRedirect, setPendingTrainingMapRedirect] =
    useState(false);
  const [categoryDoc, setCategoryDoc] = useState({
    key: "",
    name: "",
    description: "",
    hint: "",
    successMessage: "",
    failMessage: "",
  });
  const [sessionUser, setSessionUser] = useState(null);
  const { user, firebaseUser, loading: authLoading } = useContext(UserContext);
  const qa = useOwnerQa();
  const { isPro: subscriptionIsPro, subscription } = usePro();
  const { session: paidSession } = usePaidTraining();
  // Real Pro is unlimited normally. For the verified owner's training QA only,
  // an explicitly simulated free account must see the seven-puzzle gate.
  const puzzleQaMode = qa.isOwner && qa.active ? qa.config.puzzleTrainingMode : "real";
  const isPro = isUnlimitedPuzzleTraining({
    hasPaidSession: Boolean(paidSession),
    qaMode: puzzleQaMode,
    actualPro: subscriptionIsPro,
    monthlyUnlimited: Boolean(dailyAccess?.unlimited),
  });
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const entryMapMode =
    searchParams.get("entryMap") === "1" ||
    searchParams.has("house") ||
    searchParams.has("ratingFrom") ||
    searchParams.has("ratingTo");
  const requestedTrainingHouseId = searchParams.get("house") || "";
  const requestedRatingFrom = normalizeTrainingRating(
    searchParams.get("ratingFrom"),
  );
  const requestedRatingToRaw = searchParams.get("ratingTo");
  const requestedRatingTo =
    requestedRatingToRaw === null
      ? null
      : normalizeTrainingRating(requestedRatingToRaw);
  const requestedTrainingHouse =
    findTrainingHouseById(requestedTrainingHouseId) ||
    findTrainingHouseByRange(requestedRatingFrom, requestedRatingTo) ||
    null;
  const guestTrack = guestMode ? getGuestTrainingTrack(guestTrackId) : null;
  const guestPuzzleCount = guestTrack?.puzzleCount || 0;
  const guestPuzzles = guestTrackPuzzles;
  const isLastGuestPuzzle =
    guestMode &&
    guestPuzzles.length > 0 &&
    guestPuzzleIndex >= guestPuzzles.length - 1;
  const isLoggedIn = Boolean(firebaseUser);
  const baseEffectiveUser = sessionUser || user;
  const effectiveUser =
    qa.active && baseEffectiveUser
      ? { ...baseEffectiveUser, rating: qa.config.rating }
      : baseEffectiveUser;
  const userLabel = guestMode
    ? "Guest player"
    : getUserDisplayName(effectiveUser, firebaseUser);
  const currentPuzzle = puzzle.current;
  const puzzleCategoryTokens = getPuzzleCategoryTokens(
    currentPuzzle,
    guestTrack,
  );
  const puzzleCategoryLabels = getPuzzleCategoryLabels(
    currentPuzzle,
    guestTrack,
  );
  const selectedCategoryKey = pickWeakestPuzzleCategoryKey(
    puzzleCategoryTokens,
    effectiveUser?.category_rating,
  );
  const categoryDisplayLabel =
    categoryDoc.name ||
    humanizeCategoryToken(selectedCategoryKey) ||
    puzzleCategoryLabels[0] ||
    "";
  const categoryLabel = guestMode
    ? categoryDisplayLabel || guestTrack?.title || "starter training"
    : categoryDisplayLabel || formatCategoryLabel(currentPuzzle?.category);
  const trainingCopy = guestMode
    ? guestTrack?.copy || getTrainingCopy(currentPuzzle)
    : getTrainingCopy(currentPuzzle);
  const descriptionTitle = categoryDisplayLabel || "Description";
  const descriptionCopy = categoryDoc.description || trainingCopy;
  const sideToMoveLabel = config.turnColor === "black" ? "Black" : "White";
  const effectiveUserRating = normalizeTrainingRating(effectiveUser?.rating);
  // The castle map must not override Hood's automatically selected Elo range.
  // If the player is 800 Elo, always show the 500–1000 house and request 700–900 puzzles.
  const selectedTrainingHouse =
    !guestMode && !paidSession && entryMapMode
      ? resolveAdaptiveTrainingHouse(requestedTrainingHouse, effectiveUserRating)
      : requestedTrainingHouse;
  const trainingRangeFrom = selectedTrainingHouse
    ? selectedTrainingHouse.ratingFrom
    : requestedRatingFrom;
  const trainingRangeTo = selectedTrainingHouse
    ? selectedTrainingHouse.ratingTo
    : requestedRatingTo;
  const userRating = guestMode ? "Guest" : formatRating(effectiveUser?.rating);
  const puzzleRating = formatRating(currentPuzzle?.rating);
  const trainingHouseLabel = formatTrainingHouseRange(selectedTrainingHouse);
  const recommendedTrainingHouse =
    !guestMode && entryMapMode
      ? findTrainingHouseByRating(effectiveUserRating)
      : null;
  const canEnterDifferentTrainingHouse =
    Boolean(selectedTrainingHouse) &&
    Boolean(recommendedTrainingHouse) &&
    recommendedTrainingHouse.id !== selectedTrainingHouse.id;
  const recommendedTrainingHouseLabel = formatTrainingHouseRange(
    recommendedTrainingHouse,
  );
  const shouldMoveDownToMap =
    !guestMode &&
    entryMapMode &&
    Boolean(selectedTrainingHouse) &&
    isTrainingHouseUndershot(effectiveUserRating, selectedTrainingHouse);
  const shouldMoveUpToMap =
    !guestMode &&
    entryMapMode &&
    Boolean(selectedTrainingHouse) &&
    isTrainingHouseOutgrown(effectiveUserRating, selectedTrainingHouse);
  const shouldReturnToMap = shouldMoveDownToMap || shouldMoveUpToMap;
  const trainingMapShift = shouldMoveDownToMap
    ? "down"
    : shouldMoveUpToMap
      ? "up"
      : "";
  const trainingMapRedirectSearch = (() => {
    const params = new URLSearchParams();

    if (effectiveUserRating !== null) {
      params.set("rating", String(effectiveUserRating));
    }

    if (selectedTrainingHouse?.id) {
      params.set("fromHouse", selectedTrainingHouse.id);
    }

    if (trainingMapShift) {
      params.set("shift", trainingMapShift);
    }

    const query = params.toString();
    return query ? `/entrytraining?${query}` : "/entrytraining";
  })();
  const trainingRangeParams = entryMapMode
    ? {
        rating_from: trainingRangeFrom ?? undefined,
        rating_to: Number.isFinite(trainingRangeTo)
          ? trainingRangeTo
          : undefined,
      }
    : {};
  const isBelowTrainingHouse =
    !guestMode &&
    entryMapMode &&
    Boolean(selectedTrainingHouse) &&
    effectiveUserRating !== null &&
    effectiveUserRating < selectedTrainingHouse.ratingFrom;
  const puzzleTrialActive = puzzleQaMode === "real" && Boolean(dailyAccess?.trialActive);
  const trialDaysLeft = Math.max(1, Number(dailyAccess?.trialDaysLeft) || 1);
  const trialTrainingLabel = {
    pl: `Darmowy tydzień · Pozostało: ${trialDaysLeft} ${trialDaysLeft === 1 ? "dzień" : "dni"} · Puzzle bez limitu`,
    es: `Semana gratis · ${trialDaysLeft} ${trialDaysLeft === 1 ? "día restante" : "días restantes"} · Puzzles ilimitados`,
    en: `Free week · ${trialDaysLeft} ${trialDaysLeft === 1 ? "day" : "days"} left · Unlimited puzzles`,
  }[language] || `Free week · ${trialDaysLeft} days left · Unlimited puzzles`;
  const planLabel = paidSession
    ? ({ pl: "Opłacona sesja", es: "Sesión pagada" }[language] || "Paid session")
    : subscriptionIsPro ? "Pro"
      : puzzleTrialActive ? ({ pl: "Darmowy tydzień", es: "Semana gratis" }[language] || "Free week")
        : dailyAccess?.unlimited ? ({ pl: "Trening bez limitu", es: "Entrenamiento ilimitado" }[language] || "Unlimited training")
        : "Free";
  const planSource =
    subscription?.provider && subscription.provider !== "none"
      ? subscription.provider
      : subscription?.source && subscription.source !== "none"
        ? subscription.source
        : null;
  const solvedPuzzlesTowardNextAd = solvedPuzzleCount % FREE_TRAINING_AD_EVERY;
  const puzzlesUntilNextAd = isPro
    ? 0
    : solvedPuzzlesTowardNextAd === 0
      ? FREE_TRAINING_AD_EVERY
      : FREE_TRAINING_AD_EVERY - solvedPuzzlesTowardNextAd;
  const showTrainingAd =
    !isPro &&
    openedAdBreakCount !== null &&
    solvedPuzzleCount === openedAdBreakCount;
  const showTrainingAdPopup = showAdPopup && showTrainingAd && !alertInfo.show;
  const showTrainingOverlay = alertInfo.show || showTrainingAdPopup;
  const adRenderFailed =
    trainingAdRenderState === "script_error" ||
    trainingAdRenderState === "render_error" ||
    trainingAdRenderState === "script_wait_timeout" ||
    trainingAdRenderState === "no_fill";
  const adCloseLocked =
    showTrainingAdPopup && adCloseCountdown > 0 && !adRenderFailed;
  const adCloseLabel = adCloseLocked
    ? `Close in ${adCloseCountdown}s`
    : adRenderFailed
      ? "Skip ad"
      : "Close ad";
  const displayedGuestPuzzleCount =
    guestPuzzles.length || guestPuzzleCount || 1;
  const totalMoves = currentPuzzle?.after_pgn?.length || 0;
  const progressWidth = puzzleFinished
    ? 100
    : totalMoves
      ? Math.max(
          22,
          Math.min(88, Math.round((moveNr.current / totalMoves) * 100)),
        )
      : 18;

  useEffect(() => {
    setSessionUser(user || null);
  }, [user]);

  useEffect(() => {
    // Heal stale bookmarked routes such as /play?house=cottage-0-500
    // once the saved rating is known. Keep paid legacy sessions unchanged.
    if (
      !guestMode && !paidSession && entryMapMode &&
      requestedTrainingHouse && selectedTrainingHouse &&
      requestedTrainingHouse.id !== selectedTrainingHouse.id
    ) {
      navigate(`/play?${buildTrainingHouseQuery(selectedTrainingHouse)}`, { replace: true });
    }
  }, [
    guestMode, paidSession, entryMapMode, requestedTrainingHouse,
    selectedTrainingHouse, navigate,
  ]);

  useEffect(() => {
    let cancelled = false;

    async function loadCategoryDoc() {
      if (!selectedCategoryKey) {
        setCategoryDoc({
          key: "",
          name: "",
          description: "",
          hint: "",
          successMessage: "",
          failMessage: "",
        });
        return;
      }

      try {
        const categoryInfo = await getCategoryInfo(
          selectedCategoryKey,
          language,
        );

        if (cancelled) {
          return;
        }

        setCategoryDoc({
          key: selectedCategoryKey,
          name:
            resolveLocalizedField(categoryInfo, "name", language) ||
            resolveLocalizedField(categoryInfo, "title", language) ||
            humanizeCategoryToken(selectedCategoryKey),
          description: resolveLocalizedField(
            categoryInfo,
            "description",
            language,
            "",
          ),
          hint: resolveLocalizedField(categoryInfo, "hint", language, ""),
          successMessage:
            resolveLocalizedField(categoryInfo, "success_message", language) ||
            resolveLocalizedField(categoryInfo, "successMessage", language, ""),
          failMessage:
            resolveLocalizedField(categoryInfo, "fail_message", language) ||
            resolveLocalizedField(categoryInfo, "failMessage", language, ""),
        });
      } catch (error) {
        if (cancelled) {
          return;
        }

        setCategoryDoc({
          key: selectedCategoryKey,
          name: humanizeCategoryToken(selectedCategoryKey),
          description: "",
          hint: "",
          successMessage: "",
          failMessage: "",
        });
      }
    }

    loadCategoryDoc();

    return () => {
      cancelled = true;
    };
  }, [language, selectedCategoryKey]);
  const requiresPuzzlePaywall = nextPuzzleShouldShowPaywall({
    guestMode,
    isLastGuestPuzzle,
    unlimited: isPro,
    remainingToday: dailyAccess?.remainingToday ?? null,
  });
  const nextActionLabel = requiresPuzzlePaywall
    ? "Unlock training" : shouldReturnToMap ? "Back to Map" : "Next Puzzle";
  const hintPreviewConfig = buildHintPreviewConfig(
    hintStartFen,
    hintMoves,
    hintStep,
    config.orientation,
  );

  // Function to hide the alert
  const hideAlert = () => {
    setAlertInfo((currentAlert) => ({ ...currentAlert, show: false }));
  };

  const hideAdPopup = () => {
    if (adCloseLocked) {
      return;
    }
    setShowAdPopup(false);
  };

  const enterTrainingHouse = (house) => {
    if (!house) {
      return;
    }

    navigate(`/play?${buildTrainingHouseQuery(house)}`);
  };

  const syncSessionUser = (nextUser) => {
    if (nextUser && typeof nextUser === "object") {
      setSessionUser(nextUser);
    }
  };

  const returnToTrainingMap = () => {
    setPendingTrainingMapRedirect(false);

    const params = new URLSearchParams();

    if (effectiveUserRating !== null) {
      params.set("rating", String(effectiveUserRating));
    }

    if (selectedTrainingHouse?.id) {
      params.set("fromHouse", selectedTrainingHouse.id);
    }

    if (trainingMapShift) {
      params.set("shift", trainingMapShift);
    }

    const query = params.toString();
    navigate(query ? `/entrytraining?${query}` : "/entrytraining", {
      replace: true,
    });
  };

  useEffect(() => {
    if (!shouldReturnToMap) {
      setPendingTrainingMapRedirect(false);
      return undefined;
    }

    if (alertInfo.show) {
      setPendingTrainingMapRedirect(true);
    }
  }, [alertInfo.show, shouldReturnToMap]);

  useEffect(() => {
    if (!pendingTrainingMapRedirect || !shouldReturnToMap) {
      return undefined;
    }

    const redirectTimer = window.setTimeout(() => {
      returnToTrainingMap();
    }, HOUSE_SHIFT_REDIRECT_DELAY_MS);

    return () => {
      window.clearTimeout(redirectTimer);
    };
  }, [
    effectiveUserRating,
    navigate,
    pendingTrainingMapRedirect,
    selectedTrainingHouse,
    shouldReturnToMap,
    trainingMapShift,
  ]);

  useEffect(() => {
    setRequestedPuzzleId(puzzleID || null);
    setLoadMode(puzzleID ? "specific" : "feed");
  }, [puzzleID]);

  useEffect(() => {
    if (!guestMode) {
      setGuestTrackPuzzles([]);
      return;
    }

    setGuestTrackPuzzles([]);
    setGuestPuzzleIndex(guestTrackResumeIndex(guestTrackId));
    setGuestUsedToday(guestDailyQuota().usedToday);
    setLoadNonce(0);
  }, [guestMode, guestTrackId]);

  useEffect(() => {
    if (!showTrainingAdPopup || isPro) {
      setAdCloseCountdown(0);
      setTrainingAdRenderState("idle");
      return;
    }

    setAdCloseCountdown(FREE_TRAINING_AD_CLOSE_DELAY_SECONDS);

    const countdownTimer = window.setInterval(() => {
      setAdCloseCountdown((currentCountdown) =>
        currentCountdown <= 1 ? 0 : currentCountdown - 1,
      );
    }, 1000);

    return () => {
      window.clearInterval(countdownTimer);
    };
  }, [isPro, openedAdBreakCount, showTrainingAdPopup]);

  function registerSolvedPuzzle() {
    // The seven free daily puzzles should not be interrupted by ads.
    if (guestMode || firebaseUser || isPro) {
      return;
    }

    const nextSolvedCount = solvedPuzzleCount + 1;
    setSolvedPuzzleCount(nextSolvedCount);

    if (nextSolvedCount % FREE_TRAINING_AD_EVERY === 0) {
      setOpenedAdBreakCount(nextSolvedCount);
      setShowAdPopup(true);
    }

    try {
      window.localStorage.setItem(
        FREE_TRAINING_SOLVE_COUNTER_KEY,
        String(nextSolvedCount),
      );
    } catch (error) {
      console.error("Failed to persist free training solve counter:", error);
    }
  }

  function checkPromotion(game, orig, dest) {
    const pgn = orig.concat(dest);
    if (!(game.get(orig).type === "p")) return pgn;
    if (
      (game.get(orig).color === "w" && orig[1] == 7 && dest[1] == 8) ||
      (game.get(orig).color === "b" && orig[1] == 2 && dest[1] == 1)
    ) {
      setPromoting(pgn);
      return false;
    }
    return pgn;
  }

  function promotion(pgn) {
    setPromoting(false);
    makeUserMove(pgn);
  }

  function updateGame(fen) {
    const currentFen = fen || puzzle.current?.fenPosition || EMPTY_BOARD_FEN;
    const orientationSource = puzzle.current?.fenPosition || currentFen;
    const sideToMove = orientationSource.split(" ")[1];

    const newConfig = {
      fen: currentFen,
      orientation: sideToMove === "w" ? "black" : "white",
      turnColor: toColor(game),
      movable: {
        color: toColor(game),
        dests: toDests(game),
        events: {
          after: userMove,
        },
      },
    };

    setConfig(newConfig);
  }

  async function submitPuzzleResult(id, pgn, hint, lang) {
    if (!firebaseUser || paidSession) return checkPuzzleResult(id, pgn, hint, lang);
    const result = await puzzleTrainingRequest("check", {
      params: puzzleQaMode !== "real" ? { qaMode: puzzleQaMode, qaRating: qa.config.rating } : undefined,
      body: { puzzleId: id, after_pgn: pgn, hint, lang },
    });
    if (result.dailyAccess) setDailyAccess(result.dailyAccess);
    if (puzzleQaMode === "real") {
      trackUsageEvent("puzzle_training_attempt", {
        meta: { kind: hint ? "hint" : "answer", hasSubscription: Boolean(result.dailyAccess?.unlimited && !result.dailyAccess?.trialActive) },
      });
    }
    return result;
  }

  useEffect(() => {
    if (authLoading && !guestMode) return;

    if (!isLoggedIn && !guestMode) {
      setIsLoadingPuzzle(false);
      setLoadError("Log in to load your saved training puzzle.");
      return;
    }

    if (isBelowTrainingHouse && !alertInfo.show) {
      returnToTrainingMap();
      return;
    }

    if (alertInfo.show) {
      return;
    }

    let cancelled = false;
    const loadPuzzle = async () => {
      try {
        setIsLoadingPuzzle(true);
        setLoadError("");
        setPuzzleFinished(false);
        setActivePanel("idle");
        setHintCopy("");
        setHintStartFen("");
        setHintMoves([]);
        setHintStep(0);
        setPendingTrainingMapRedirect(false);
        moveNr.current = 0;
        setAlertInfo((currentAlert) => ({ ...currentAlert, show: false }));

        let guestPayloads = guestPuzzles;

        if (guestMode) {
          if (!guestPayloads.length) {
            guestPayloads = await loadGuestTrackPuzzles(guestTrack, language);

            if (cancelled) return;

            setGuestTrackPuzzles(guestPayloads);
          }

          if (!guestPayloads[guestPuzzleIndex]) {
            throw new Error("This guest starter puzzle is not available.");
          }
        }

        let payload = null;

        if (guestMode) {
          payload = guestPayloads[guestPuzzleIndex];
        } else if (firebaseUser && !paidSession) {
          const chosenParams = {
            lang: language,
            requestId: currentDailyPuzzleToken(firebaseUser.uid),
            ...(selectedTrainingHouse ? { house: backendTrainingHouseId(selectedTrainingHouse) } : {}),
            ...(qa.isOwner && qa.active ? { qaRating: qa.config.rating } : {}),
            ...(puzzleQaMode !== "real" ? { qaMode: puzzleQaMode } : {}),
            ...(loadMode === "specific" && requestedPuzzleId ? { puzzleId: requestedPuzzleId } : {}),
          };
          payload = await puzzleTrainingRequest("next", { params: chosenParams });
          if (!cancelled && payload.dailyAccess) setDailyAccess(payload.dailyAccess);
          if (puzzleQaMode === "real") {
            trackUsageEvent("puzzle_training_issued", { meta: { freeOrPaid: payload.dailyAccess?.trialActive ? "trial" : payload.dailyAccess?.unlimited ? "paid" : "free" } });
          }
        } else if (loadMode === "specific" && requestedPuzzleId) {
          payload = await getPuzzle(requestedPuzzleId, undefined, language);
        } else if (entryMapMode) {
          try {
            payload = await getFirstPuzzle(language, trainingRangeParams);
          } catch (trainingError) {
            payload = await getPuzzle(
              undefined,
              undefined,
              language,
              trainingRangeParams,
            );
          }
        } else {
          payload = await getFirstPuzzle(language);
        }

        const nextPuzzle = guestMode
          ? payload
          : normalizePuzzle(payload, language);

        if (!nextPuzzle) {
          throw new Error("Training endpoint returned no puzzle data.");
        }

        if (!nextPuzzle.fenPosition) {
          throw new Error("Training endpoint returned a puzzle without FEN.");
        }

        if (!nextPuzzle.after_pgn.length) {
          throw new Error("Training endpoint returned a puzzle without moves.");
        }

        // A backend or cached-session regression must never give an 800 Elo
        // player a 2206 Elo puzzle. Don't render or consume that position.
        // Random paid passes deliberately remain random by their own contract.
        const adaptiveMode = !guestMode &&
          (!paidSession || paidSession.mode !== "random");
        const playerElo = Number(effectiveUser?.rating);
        const taskElo = Number(nextPuzzle.rating);
        if (adaptiveMode && Number.isFinite(playerElo) && playerElo > 0 &&
            (!Number.isFinite(taskElo) || Math.abs(taskElo - playerElo) > 100)) {
          throw new Error(
            "Hood couldn't find a suitable puzzle within 100 Elo points. " +
            "Please retry to choose a new position."
          );
        }

        if (cancelled) return;

        if (guestMode) {
          const result = reserveGuestPuzzle(extractPuzzleId(nextPuzzle), guestTrackId, guestPuzzleIndex);
          if (!result.allowed) {
            navigate("/training?dailyLimit=1", { replace: true });
            return;
          }
          setGuestUsedToday(result.usedToday);
        }
        puzzle.current = nextPuzzle;
        game.load(nextPuzzle.fenPosition);
        setConfig({
          orientation: game.turn() === "b" ? "white" : "black",
        });
        updateGame(nextPuzzle.fenPosition);
        computerMove(true);
      } catch (error) {
        if (cancelled) return;
        if (error?.status === 402 || error?.message === "daily_free_limit_reached") {
          navigate("/training?dailyLimit=1", { replace: true });
          return;
        }
        console.error("Error while loading puzzle:", error);
        puzzle.current = null;
        setLoadError(
          guestMode
            ? (error?.message || "Failed to load guest starter puzzles.")
            : trainingErrorMessage(error, effectiveUserRating),
        );
      } finally {
        if (!cancelled) {
          setIsLoadingPuzzle(false);
        }
      }
    };

    loadPuzzle();

    return () => {
      cancelled = true;
    };
  }, [
    authLoading,
    isBelowTrainingHouse,
    puzzleQaMode,
    qa.config.rating,
    guestMode,
    guestPuzzleIndex,
    guestPuzzles,
    guestPuzzleCount,
    guestTrack,
    isLoggedIn,
    language,
    loadMode,
    entryMapMode,
    selectedTrainingHouse,
    trainingRangeFrom,
    trainingRangeTo,
    requestedPuzzleId,
    loadNonce,
    alertInfo.show,
  ]);

  function finishSuccessfulAttempt(
    currentPuzzle,
    puzzleId,
    preferLocalFeedback = false,
  ) {
    const localSuccessMessage = resolveFeedbackMessage(
      null,
      currentPuzzle,
      "success",
      language,
      { categoryMessage: categoryDoc.successMessage },
    );
    const successAlert = (result = null, forceLocalFeedback = false) => ({
      show: true,
      variant: "success",
      user:
        forceLocalFeedback || Number(result?.addedOrRestRating ?? 0) < 0
          ? effectiveUser
          : result?.user || effectiveUser,
      addedOrRestRating: forceLocalFeedback
        ? 0
        : Math.max(0, Number(result?.addedOrRestRating ?? 0)),
      puzzleSuccessMessage: forceLocalFeedback
        ? localSuccessMessage
        : resolveFeedbackMessage(result, currentPuzzle, "success", language, {
            categoryMessage: categoryDoc.successMessage,
          }),
      success: "true",
      fen: currentPuzzle.fenPosition,
      after_pgn: currentPuzzle.after_pgn,
    });

    if (puzzleId) {
      submitPuzzleResult(puzzleId, toPgnHistory(game), false, language)
        .then((result) => {
          const returnedRatingDelta = Number(result?.addedOrRestRating ?? 0);
          const hasInvalidNegativeSuccessDelta =
            Number.isFinite(returnedRatingDelta) && returnedRatingDelta < 0;

          if (!hasInvalidNegativeSuccessDelta) {
            syncSessionUser(result?.user);
          }

          registerSolvedPuzzle();
          setAlertInfo(
            successAlert(
              result,
              preferLocalFeedback || hasInvalidNegativeSuccessDelta,
            ),
          );
        })
        .catch(() => {
          registerSolvedPuzzle();
          setAlertInfo(successAlert(null, true));
        });
      return;
    }

    registerSolvedPuzzle();
    setAlertInfo(successAlert(null, true));
  }

  function makeUserMove(pgn, hint = false) {
    const currentPuzzle = puzzle.current;
    const puzzleId = extractPuzzleId(currentPuzzle);
    const expectedMove = currentPuzzle?.after_pgn?.[moveNr.current];
    const matchesExpectedMove =
      normalizeMoveNotation(pgn) === normalizeMoveNotation(expectedMove);
    const solvesByCheckmate =
      !matchesExpectedMove && moveSolvesByCheckmate(pgn, game);

    if (!currentPuzzle) {
      return;
    }

    if (hint) {
      if (puzzleId) {
        submitPuzzleResult(puzzleId, toPgnHistory(game), true, language)
          .then((result) => {
            syncSessionUser(result?.user);
            setAlertInfo({
              show: true,
              variant: "warning",
              user: result?.user || effectiveUser,
              addedOrRestRating: result?.addedOrRestRating ?? 0,
              puzzleSuccessMessage: resolveFeedbackMessage(
                result,
                currentPuzzle,
                "hint",
                language,
              ),
              success: "hint",
              fen: currentPuzzle.fenPosition,
              after_pgn: currentPuzzle.after_pgn,
              config: config,
            });
          })
          .catch(() => {
            setAlertInfo({
              show: true,
              variant: "warning",
              user: effectiveUser,
              addedOrRestRating: 0,
              puzzleSuccessMessage:
                "Failed to fetch hint from backend. Try again later or solve the current puzzle.",
              success: "hint",
              fen: currentPuzzle.fenPosition,
              after_pgn: currentPuzzle.after_pgn,
              config: config,
            });
          });
      } else {
        setAlertInfo({
          show: true,
          variant: "warning",
          user: effectiveUser,
          addedOrRestRating: 0,
          puzzleSuccessMessage: resolveFeedbackMessage(
            null,
            currentPuzzle,
            "hint",
          ),
          success: "hint",
          fen: currentPuzzle.fenPosition,
          after_pgn: currentPuzzle.after_pgn,
          config: config,
        });
      }
      setPuzzleFinished(true);
      return;
    }

    if (matchesExpectedMove || solvesByCheckmate) {
      game.move(pgn);

      if (
        solvesByCheckmate ||
        moveNr.current === currentPuzzle.after_pgn.length - 1
      ) {
        finishSuccessfulAttempt(currentPuzzle, puzzleId, solvesByCheckmate);
        updateGame(game.fen());
        setPuzzleFinished(true);
      } else {
        moveNr.current++;
        computerMove();
      }
    } else {
      // Handles incorrect moves or puzzle failures
      if (puzzleId) {
        submitPuzzleResult(puzzleId, toPgnHistory(game), false, language)
          .then((result) => {
            syncSessionUser(result?.user);
            setAlertInfo({
              show: true,
              variant: "danger",
              user: result?.user || effectiveUser,
              addedOrRestRating: result?.addedOrRestRating ?? 0,
              puzzleSuccessMessage: resolveFeedbackMessage(
                result,
                currentPuzzle,
                "fail",
                language,
                { categoryMessage: categoryDoc.failMessage },
              ),
              success: "false",
              fen: currentPuzzle.fenPosition,
              after_pgn: currentPuzzle.after_pgn,
            });
          })
          .catch(() => {
            setAlertInfo({
              show: true,
              variant: "danger",
              user: effectiveUser,
              addedOrRestRating: 0,
              puzzleSuccessMessage: resolveFeedbackMessage(
                null,
                currentPuzzle,
                "fail",
                language,
                { categoryMessage: categoryDoc.failMessage },
              ),
              success: "false",
              fen: currentPuzzle.fenPosition,
              after_pgn: currentPuzzle.after_pgn,
            });
          });
      } else {
        setAlertInfo({
          show: true,
          variant: "danger",
          user: effectiveUser,
          addedOrRestRating: 0,
          puzzleSuccessMessage: resolveFeedbackMessage(
            null,
            currentPuzzle,
            "fail",
            language,
            { categoryMessage: categoryDoc.failMessage },
          ),
          success: "false",
          fen: currentPuzzle.fenPosition,
          after_pgn: currentPuzzle.after_pgn,
        });
      }

      setPuzzleFinished(true);
      updateGame(game.fen());
    }
  }

  function repeat() {
    setAlertInfo((currentAlert) => ({ ...currentAlert, show: false }));
    setActivePanel("idle");
    setHintCopy("");
    setHintStartFen("");
    setHintMoves([]);
    setHintStep(0);
    const currentPuzzleId = extractPuzzleId(puzzle.current);
    setRequestedPuzzleId(currentPuzzleId || null);
    setLoadMode(currentPuzzleId ? "specific" : "feed");
    setLoadNonce((currentNonce) => currentNonce + 1);
  }

  function userMove(orig, dest) {
    const pgn = checkPromotion(game, orig, dest);
    if (!pgn) return;
    makeUserMove(pgn);
  }

  function computerMove(firstLoad = false) {
    const move = puzzle.current?.after_pgn?.[firstLoad ? 0 : moveNr.current];

    if (!move) {
      return;
    }

    if (firstLoad) {
      moveNr.current = 1;
    } else {
      moveNr.current++;
    }

    game.move(move);
    updateGame(game.fen());
  }

  async function shareTraining() {
    const trainingUrl = window.location.origin + "/training";
    const details = {
      title: "Chesshood Training",
      text: "Solve 7 free chess puzzles every day with Hood, your personal chess trainer.",
      url: trainingUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(details);
        setShareNotice("");
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(trainingUrl);
        setShareNotice("Training link copied");
      } else {
        setShareNotice("Share chesshood.com/training with a friend");
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        setShareNotice("Share chesshood.com/training with a friend");
      }
    }
  }

  function nextPuzzle() {
    setAlertInfo((currentAlert) => ({ ...currentAlert, show: false }));

    if (guestMode) {
      if (requiresPuzzlePaywall) {
        navigate("/training?dailyLimit=1", { replace: true });
        return;
      }

      setActivePanel("idle");
      setHintCopy("");
      setHintStartFen("");
      setHintMoves([]);
      setHintStep(0);
      setPendingTrainingMapRedirect(false);
      setGuestPuzzleIndex((currentIndex) => currentIndex + 1);
      return;
    }

    if (requiresPuzzlePaywall) {
      navigate("/training?dailyLimit=1");
      return;
    }

    if (firebaseUser && !paidSession) nextDailyPuzzleToken(firebaseUser.uid);

    if (shouldReturnToMap) {
      returnToTrainingMap();
      return;
    }

    setActivePanel("idle");
    setHintCopy("");
    setHintStartFen("");
    setHintMoves([]);
    setHintStep(0);
    setPendingTrainingMapRedirect(false);
    setRequestedPuzzleId(null);
    setLoadMode("feed");
    setLoadNonce((currentNonce) => currentNonce + 1);

    if (!entryMapMode) {
      navigate("/play", { replace: true });
    }
  }

  function hint() {
    const currentPuzzle = puzzle.current;
    if (!currentPuzzle) {
      return;
    }

    if (activePanel === "hint") {
      setActivePanel("idle");
      return;
    }

    const localHintCopy = getHintCopy(currentPuzzle, moveNr.current);
    const remainingMoves = currentPuzzle.after_pgn.slice(moveNr.current);
    setHintCopy(localHintCopy);
    setHintStartFen(game.fen());
    setHintMoves(remainingMoves);
    setHintStep(0);
    setActivePanel("hint");

    const puzzleId = extractPuzzleId(currentPuzzle);
    if (!puzzleId) {
      return;
    }

    submitPuzzleResult(puzzleId, toPgnHistory(game), true, language)
      .then((result) => {
        const backendHint = resolveFeedbackMessage(
          result,
          currentPuzzle,
          "hint",
          language,
        );

        if (extractPuzzleId(puzzle.current) === puzzleId) {
          setHintCopy(String(backendHint).trim() || localHintCopy);
        }
      })
      .catch(() => {
        if (extractPuzzleId(puzzle.current) === puzzleId) {
          setHintCopy(localHintCopy);
        }
      });
  }

  function toggleDescriptionPanel() {
    setActivePanel((currentPanel) =>
      currentPanel === "description" ? "idle" : "description",
    );
  }

  function closeHintPanel() {
    setActivePanel((currentPanel) =>
      currentPanel === "hint" ? "idle" : currentPanel,
    );
  }

  useEffect(() => {
    if (activePanel !== "hint") {
      return undefined;
    }

    function handleHintPanelKeydown(event) {
      if (event.key === "Escape") {
        setActivePanel("idle");
      }
    }

    window.addEventListener("keydown", handleHintPanelKeydown);
    return () => {
      window.removeEventListener("keydown", handleHintPanelKeydown);
    };
  }, [activePanel]);


  return (
    <div className="ch-training-phone-screen ch-training-mobile-parity">
      <style>{`
        .ch-training-phone-screen {
          height: 100vh;
          height: 100dvh;
          padding: 12px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          background:
            radial-gradient(circle at top, rgba(132, 244, 154, 0.3), transparent 30%),
            radial-gradient(circle at bottom, rgba(26, 196, 154, 0.28), transparent 42%),
            linear-gradient(180deg, #173328 0%, #07140f 100%);
        }

        .ch-training-responsive-menu{
          position:fixed;
          top:max(8px, env(safe-area-inset-top));
          right:8px;
          z-index:5000;
          max-width:calc(100vw - 16px);
        }

        .ch-training-fit-stage {
          width: 100%;
          max-width: 430px;
          margin: 0 auto;
        }

        .ch-training-page {
          width: 100%;
          padding: 10px;
          border-radius: 24px;
          background: linear-gradient(180deg, rgba(41, 41, 41, 0.98), rgba(14, 14, 14, 0.99));
          box-shadow: 0 0 0 1px rgba(255,255,255,0.05), 0 24px 48px rgba(0,0,0,0.38);
          color: #f3ead4;
        }

        .ch-training-topbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 2px 2px 10px;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }

        .ch-training-brand {
          font-size: 1.5rem;
          font-weight: 900;
          color: #f4f0e3;
        }

        .ch-training-top-actions {
          display: flex;
          gap: 8px;
        }

        .ch-training-plan-row {
          display: flex;
          justify-content: center;
          margin-top: 14px;
        }

        .ch-training-plan-pill {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          border-radius: 999px;
          background: ${isPro ? "rgba(255,215,97,0.12)" : "rgba(255,255,255,0.06)"};
          color: #f6ead0;
          font-size: 0.82rem;
          font-weight: 700;
        }

        .ch-training-plan-pill strong {
          color: ${isPro ? "#ffd761" : "#f5b15b"};
        }

        .ch-training-plan-subtext {
          margin-top: 8px;
          text-align: center;
          color: ${isPro ? "#cce9c2" : "#d6c7a7"};
          font-size: 0.84rem;
          line-height: 1.45;
        }

        .ch-training-upsell {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          margin: 14px 0 6px;
          padding: 14px 16px;
          border-radius: 18px;
          background:
            radial-gradient(circle at top right, rgba(242,184,77,0.2), transparent 38%),
            linear-gradient(180deg, rgba(56,43,15,0.98), rgba(26,19,8,0.98));
          border: 1px solid rgba(242,184,77,0.28);
        }

        .ch-training-upsell-copy {
          display: grid;
          gap: 4px;
          min-width: 0;
        }

        .ch-training-upsell-copy strong {
          color: #fff2cf;
          font-size: 1rem;
          font-weight: 900;
        }

        .ch-training-upsell-copy span {
          color: #e3d6bb;
          font-size: 0.88rem;
          line-height: 1.45;
        }

        .ch-training-upsell-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 110px;
          min-height: 44px;
          padding: 0 16px;
          border-radius: 14px;
          background: linear-gradient(180deg, #f2b84d, #da8b26);
          color: #1b1204;
          text-decoration: none;
          font-weight: 900;
          white-space: nowrap;
        }

        .ch-training-icon-button {
          width: 38px;
          height: 38px;
          border: 0;
          border-radius: 12px;
          background: rgba(255,255,255,0.08);
          color: #f8f1df;
        }

        .ch-training-copy-block {
          padding: 14px 6px 10px;
          text-align: center;
        }

        .ch-training-category {
          margin: 0 0 8px;
          color: #e0b432;
          font-size: 1.05rem;
          font-weight: 900;
          text-transform: lowercase;
        }

        .ch-training-copy {
          margin: 0;
          color: #ddd2bf;
          line-height: 1.5;
        }

        .ch-training-ratings {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
          margin-bottom: 12px;
        }

        .ch-training-rating {
          padding: 10px 12px;
          border-radius: 14px;
          background: rgba(0,0,0,0.34);
          text-align: center;
          font-weight: 900;
        }

        .ch-training-rating-user { color: #f2c23c; }
        .ch-training-rating-puzzle { color: #ef8d41; }

        .ch-training-board-wrap {
          padding: 8px 8px 22px 22px;
          border-radius: 18px;
          background: #040404;
        }

        .ch-training-board-shell {
          width: 100%;
          aspect-ratio: 1 / 1;
          border-radius: 10px;
          overflow: visible;
          background: #000;
        }

        .ch-training-board-shell .ch-training-board,
        .ch-training-board-shell .cg-wrap,
        .ch-training-board-shell cg-container,
        .ch-training-board-shell cg-board {
          width: 100% !important;
          height: 100% !important;
          max-width: none !important;
          max-height: none !important;
          margin: 0 !important;
        }

        .ch-training-board-shell .cg-wrap piece {
          background-position: center;
          background-repeat: no-repeat;
        }

        .ch-training-board-shell .cg-wrap,
        .ch-training-board-shell cg-container {
          overflow: visible !important;
        }

        .ch-training-board-shell .cg-wrap coords {
          opacity: 1;
          font-family: "Trebuchet MS", "Arial Narrow", Arial, sans-serif;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.04em;
          color: #f7fbff !important;
          text-shadow:
            0 1px 2px rgba(0,0,0,0.92),
            0 0 8px rgba(255,255,255,0.18);
        }

        .ch-training-board-shell .cg-wrap coords coord {
          display: flex;
          align-items: center;
          justify-content: center;
          color: #f7fbff !important;
        }

        .ch-training-board-shell .cg-wrap coords.ranks {
          left: -18px;
          top: 0;
          width: 14px;
          height: 100%;
        }

        .ch-training-board-shell .cg-wrap coords.ranks coord {
          transform: none;
        }

        .ch-training-board-shell .cg-wrap coords.files {
          left: 0;
          bottom: -18px;
          width: 100%;
          height: 14px;
        }

        .ch-training-board-status {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 14px;
          padding: 24px 18px;
          text-align: center;
          color: #f2e8d1;
          background: linear-gradient(180deg, rgba(32,32,32,0.94), rgba(12,12,12,0.96));
        }

        .ch-training-board-status-loading {
          justify-content: flex-start;
          padding-top: 34px;
        }

        .ch-training-board-status-hood-wrap {
          position: relative;
          width: min(100%, 280px);
          padding-top: 26px;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          overflow: visible;
        }

        .ch-training-board-status-hood {
          width: min(100%, 220px);
          display: block;
          transform: translateY(-10px);
          filter: drop-shadow(0 18px 28px rgba(0,0,0,0.34));
        }

        .ch-training-board-status-bubble {
          position: absolute;
          left: 50%;
          top: -8px;
          transform: translateX(-50%);
          width: min(100%, 260px);
          padding: 14px 16px;
          border-radius: 22px;
          background: rgba(255, 252, 244, 0.98);
          color: #1e2b35;
          font-weight: 900;
          line-height: 1.45;
          box-shadow: 0 16px 34px rgba(0,0,0,0.18);
        }

        .ch-training-board-status-bubble::after {
          content: "";
          position: absolute;
          left: 50%;
          bottom: -12px;
          width: 20px;
          height: 20px;
          background: rgba(255, 252, 244, 0.98);
          transform: translateX(-50%) rotate(45deg);
          border-radius: 4px;
        }

        .ch-training-board-status-actions {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 10px;
        }

        .ch-training-turn {
          margin: 14px 0 10px;
          text-align: center;
          color: #ffffff;
          font-size: 1.5rem;
          font-weight: 900;
        }

        .ch-training-progress {
          height: 8px;
          margin-bottom: 14px;
          border-radius: 999px;
          overflow: hidden;
          background: rgba(255,255,255,0.08);
        }

        .ch-training-progress-fill {
          height: 100%;
          border-radius: inherit;
          background: linear-gradient(90deg, #1ae052, #98f12f);
        }

        .ch-training-feedback .alert {
          margin-bottom: 14px;
          border-radius: 16px;
          background: rgba(26,26,26,0.96);
          color: #f6eed8;
          border: 1px solid rgba(255,255,255,0.06);
        }

        .ch-training-overlay {
          position: fixed;
          inset: 0;
          z-index: 1200;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 18px 18px calc(18px + env(safe-area-inset-bottom, 0px) + 56px);
          background: transparent;
          backdrop-filter: none;
          pointer-events: auto;
        }

        .ch-training-overlay-stack {
          width: min(100%, 640px);
          max-height: calc(100dvh - 36px - env(safe-area-inset-bottom, 0px) - 56px);
          display: grid;
          gap: 14px;
          overflow: visible;
          pointer-events: auto;
        }

        .ch-training-popup-card {
          position: relative;
          overflow: visible;
        }

        .ch-training-feedback-popup {
          overflow: visible;
        }

        .ch-training-feedback-popup .alert {
          margin: 0;
          border-radius: 22px;
          background: linear-gradient(180deg, rgba(28, 28, 28, 0.98), rgba(13, 13, 13, 0.99));
          color: #f6eed8;
          border: 1px solid rgba(255,255,255,0.08);
          box-shadow: 0 24px 48px rgba(0,0,0,0.42);
        }

        .ch-training-feedback-popup .alert-dismissible {
          padding-right: 3.5rem;
        }

        .ch-training-feedback-popup .btn-close,
        .ch-training-ad-close {
          filter: invert(1);
          opacity: 0.82;
        }

        .ch-training-ad-popup .ch-training-monetize {
          margin: 0;
          border-radius: 22px;
          box-shadow: 0 24px 48px rgba(0,0,0,0.42);
          flex: 1 1 auto;
          min-height: 0;
          overflow: auto;
        }

        .ch-training-ad-popup {
          display: flex;
          flex-direction: column;
          max-height: calc(100dvh - 36px - env(safe-area-inset-bottom, 0px) - 56px);
          overflow: hidden;
          border-radius: 22px;
          background: rgba(8, 12, 18, 0.96);
          box-shadow: 0 24px 48px rgba(0,0,0,0.42);
        }

        .ch-training-ad-close {
          position: absolute;
          top: 12px;
          right: 12px;
          z-index: 2;
          min-width: 96px;
          height: 38px;
          padding: 0 12px;
          border: 0;
          border-radius: 12px;
          background: rgba(255,255,255,0.08);
          color: #ffffff;
          font-size: 0.78rem;
          font-weight: 800;
          letter-spacing: 0.02em;
          transition:
            opacity 140ms ease,
            background 140ms ease,
            transform 140ms ease;
          font-size: 0;
          line-height: 0;
        }

        .ch-training-ad-close::after {
          content: attr(data-close-label);
          font-size: 0.78rem;
          line-height: 1;
          font-weight: 800;
          letter-spacing: 0.02em;
        }

        .ch-training-ad-close:hover {
          background: rgba(255,255,255,0.16);
          transform: translateY(-1px);
        }

        .ch-training-ad-close[aria-disabled="true"] {
          opacity: 0.74;
          cursor: not-allowed;
        }

        .ch-training-ad-close[aria-disabled="true"]:hover {
          background: rgba(255,255,255,0.08);
          transform: none;
        }

        .ch-training-ad-footer {
          z-index: 3;
          margin-top: auto;
          padding: 12px 12px calc(12px + env(safe-area-inset-bottom, 0px));
          display: grid;
          background: linear-gradient(180deg, rgba(8, 12, 18, 0), rgba(8, 12, 18, 0.96) 36%);
        }

        .ch-training-ad-footer-button {
          min-height: 48px;
          border: 0;
          border-radius: 14px;
          background: linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0.08));
          color: #fff4db;
          font-size: 0.92rem;
          font-weight: 900;
          letter-spacing: 0.01em;
        }

        .ch-training-ad-footer-button[aria-disabled="true"] {
          opacity: 0.74;
        }

        .ch-training-tabs,
        .ch-training-actions {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
        }

        .ch-training-tab,
        .ch-training-action,
        .ch-training-retry {
          min-height: 46px;
          border: 0;
          border-radius: 14px;
          font-weight: 800;
        }

        .ch-training-tab {
          background: rgba(255,255,255,0.05);
          color: #f2ead5;
        }

        .ch-training-tab-active {
          background: rgba(255,214,76,0.14);
          color: #ffd761;
        }

        .ch-training-panel {
          min-height: 96px;
          margin: 10px 0 12px;
          padding: 14px;
          border-radius: 16px;
          background: rgba(255,255,255,0.04);
          color: #e7dcc4;
        }

        .ch-training-panel p {
          margin: 0;
          line-height: 1.55;
        }

        .ch-training-monetize {
          margin: 12px 0 14px;
          padding: 14px;
          border-radius: 18px;
          background: linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03));
          border: 1px solid rgba(255,255,255,0.07);
        }

        .ch-training-monetize-kicker {
          color: #f0b454;
          font-size: 0.75rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        .ch-training-monetize-title {
          margin: 6px 0 8px;
          font-size: 1.1rem;
          color: #fff5dc;
        }

        .ch-training-monetize-copy {
          margin: 0 0 12px;
          color: #ddcfb3;
          line-height: 1.5;
        }

        .ch-training-ad-box,
        .ch-training-ad-placeholder {
          padding: 12px;
          border-radius: 14px;
          background: rgba(0,0,0,0.26);
          border: 1px dashed rgba(255,255,255,0.12);
          color: #d3c6ac;
        }

        .ch-training-ad-label {
          margin-bottom: 8px;
          color: rgba(255,255,255,0.55);
          font-size: 0.72rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        .ch-training-monetize-actions {
          display: flex;
          justify-content: flex-start;
          margin-top: 12px;
        }

        .ch-training-upgrade-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 44px;
          padding: 0 16px;
          border-radius: 12px;
          background: linear-gradient(180deg, #f2b84d, #da8b26);
          color: #1a1204;
          font-weight: 900;
          text-decoration: none;
        }

        @media (max-width: 420px) {
          .ch-training-upsell {
            align-items: stretch;
            flex-direction: column;
          }

          .ch-training-upsell-link {
            width: 100%;
          }
        }

        @media (max-width: 480px) {
          .ch-training-phone-screen {
            padding: 8px;
          }

          .ch-training-page {
            width: 100%;
            padding: 8px;
            border-radius: 20px;
          }

          .ch-training-topbar {
            padding: 2px 2px 8px;
          }

          .ch-training-brand {
            font-size: 1.32rem;
          }

          .ch-training-plan-row {
            margin-top: 8px;
          }

          .ch-training-plan-pill {
            gap: 6px;
            padding: 6px 12px;
            font-size: 0.75rem;
          }

          .ch-training-plan-subtext {
            margin-top: 6px;
            font-size: 0.76rem;
            line-height: 1.35;
          }

          .ch-training-upsell {
            gap: 10px;
            margin: 10px 0 4px;
            padding: 10px 12px;
          }

          .ch-training-upsell-copy strong {
            font-size: 0.94rem;
          }

          .ch-training-upsell-copy span {
            font-size: 0.8rem;
            line-height: 1.3;
          }

          .ch-training-upsell-link {
            min-width: 96px;
            min-height: 40px;
            padding: 0 14px;
          }

          .ch-training-copy-block {
            padding: 8px 4px 4px;
          }

          .ch-training-category {
            margin-bottom: 4px;
            font-size: 0.98rem;
          }

          .ch-training-copy {
            display: none;
          }

          .ch-training-ratings {
            gap: 6px;
            margin-bottom: 8px;
          }

          .ch-training-rating {
            padding: 8px 10px;
            font-size: 0.88rem;
          }

          .ch-training-board-wrap {
            padding: 6px;
          }

          .ch-training-turn {
            margin: 10px 0 8px;
            font-size: 1.28rem;
          }

          .ch-training-progress {
            margin-bottom: 10px;
          }

          .ch-training-tabs,
          .ch-training-actions {
            gap: 8px;
          }

          .ch-training-tab,
          .ch-training-action,
          .ch-training-retry {
            min-height: 42px;
            font-size: 0.95rem;
          }

          .ch-training-panel {
            min-height: auto;
            margin: 8px 0 10px;
            padding: 10px 12px;
          }

          .ch-training-panel p {
            font-size: 0.88rem;
            line-height: 1.4;
          }

          .ch-training-user {
            margin-top: 8px;
            font-size: 0.76rem;
          }
        }

        @media (max-width: 480px) and (max-height: 920px) {
          .ch-training-phone-screen {
            padding: 6px;
          }

          .ch-training-page {
            padding: 6px;
            border-radius: 18px;
          }

          .ch-training-topbar {
            padding: 2px 2px 6px;
          }

          .ch-training-plan-row {
            margin-top: 6px;
          }

          .ch-training-plan-row,
          .ch-training-plan-subtext,
          .ch-training-upsell,
          .ch-training-tabs,
          .ch-training-panel,
          .ch-training-user {
            display: none;
          }

          .ch-training-copy-block {
            padding: 6px 4px 4px;
          }

          .ch-training-category {
            margin-bottom: 0;
            font-size: 0.92rem;
          }

          .ch-training-ratings {
            gap: 6px;
            margin-bottom: 6px;
          }

          .ch-training-rating {
            padding: 6px 8px;
            font-size: 0.82rem;
          }

          .ch-training-board-wrap {
            padding: 4px;
          }

          .ch-training-board-shell {
            max-width: min(100%, 330px);
            margin: 0 auto;
          }

          .ch-training-turn {
            margin: 8px 0 6px;
            font-size: 1.14rem;
          }

          .ch-training-progress {
            margin-bottom: 8px;
          }

          .ch-training-actions {
            position: sticky;
            bottom: 0;
            padding-top: 6px;
            background: linear-gradient(180deg, rgba(14, 14, 14, 0), rgba(14, 14, 14, 0.96) 28%);
            z-index: 1;
          }
        }

        .ch-training-action-repeat,
        .ch-training-retry {
          background: #e7e7e7;
          color: #202020;
        }

        .ch-training-action-next {
          background: #5f5f5f;
          color: #ffffff;
        }

        .ch-training-action-next:not(:disabled) {
          background: linear-gradient(180deg, #f2b84d, #da8b26);
          color: #1a1204;
        }

        .ch-training-user {
          margin-top: 12px;
          text-align: center;
          color: rgba(255,255,255,0.55);
          font-size: 0.82rem;
        }
        .ch-training-fit-stage {
          max-width: 920px;
        }

        .ch-training-page {
          position: relative;
          min-height: calc(100dvh - 24px);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 28px 24px 24px;
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 28px;
          overflow: hidden;
          background:
            radial-gradient(circle at 18% 18%, rgba(116, 255, 160, 0.32), transparent 30%),
            radial-gradient(circle at 84% 24%, rgba(38, 213, 184, 0.28), transparent 34%),
            linear-gradient(180deg, rgba(5, 28, 20, 0.02), rgba(7, 33, 25, 0.12) 34%, rgba(5, 24, 18, 0.26)),
            url('/trainingtlo.png');
          background-size: cover;
          background-position: center center;
          box-shadow:
            0 0 32px rgba(44, 189, 144, 0.12),
            0 24px 50px rgba(0,0,0,0.34);
        }

        .ch-training-side-rail {
          position: absolute;
          left: 18px;
          top: 34px;
          z-index: 2;
          display: grid;
          gap: 14px;
        }

        .ch-training-rail-button {
          width: 58px;
          height: 58px;
          border: 0;
          border-radius: 18px;
          background: rgba(12, 48, 37, 0.72);
          color: #effff7;
          backdrop-filter: saturate(150%) blur(8px);
          box-shadow:
            0 0 18px rgba(57, 219, 162, 0.2),
            0 12px 22px rgba(0,0,0,0.18);
          transition:
            transform 140ms ease,
            box-shadow 140ms ease,
            background 140ms ease;
        }

        .ch-training-rail-button:hover:not(:disabled) {
          transform: translateY(-1px);
        }

        .ch-training-rail-button-home {
          background: linear-gradient(180deg, rgba(18, 77, 89, 0.88), rgba(9, 46, 57, 0.94));
          color: #e6fbff;
          box-shadow:
            0 0 18px rgba(83, 220, 255, 0.24),
            0 12px 22px rgba(0,0,0,0.18);
        }

        .ch-training-rail-button-home:hover:not(:disabled) {
          background: linear-gradient(180deg, rgba(25, 93, 107, 0.92), rgba(10, 54, 68, 0.96));
          box-shadow:
            0 0 22px rgba(83, 220, 255, 0.32),
            0 14px 24px rgba(0,0,0,0.2);
        }

        .ch-training-rail-button-login {
          background: linear-gradient(180deg, rgba(122, 94, 18, 0.88), rgba(82, 57, 9, 0.94));
          color: #fff4cf;
          box-shadow:
            0 0 18px rgba(255, 212, 83, 0.24),
            0 12px 22px rgba(0,0,0,0.18);
        }

        .ch-training-rail-button-login:hover:not(:disabled) {
          background: linear-gradient(180deg, rgba(144, 110, 20, 0.92), rgba(99, 68, 11, 0.96));
          box-shadow:
            0 0 22px rgba(255, 212, 83, 0.32),
            0 14px 24px rgba(0,0,0,0.2);
        }

        .ch-training-rail-button-exit {
          background: linear-gradient(180deg, rgba(107, 36, 36, 0.9), rgba(72, 16, 16, 0.96));
          color: #ffe0e0;
          box-shadow:
            0 0 18px rgba(255, 105, 105, 0.24),
            0 12px 22px rgba(0,0,0,0.18);
        }

        .ch-training-rail-button-exit:hover:not(:disabled) {
          background: linear-gradient(180deg, rgba(128, 40, 40, 0.94), rgba(84, 18, 18, 0.98));
          box-shadow:
            0 0 22px rgba(255, 105, 105, 0.32),
            0 14px 24px rgba(0,0,0,0.2);
        }

        .ch-training-board-stage {
          flex: 1;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 0 14px;
          z-index: 1;
        }

        .ch-training-side-hood {
          position: absolute;
          left: 22px;
          bottom: 42px;
          z-index: 1;
          width: 76px;
          pointer-events: none;
          filter: drop-shadow(0 10px 16px rgba(0,0,0,0.28));
          opacity: 0.96;
        }

        .ch-training-board-header {
          display: flex;
          justify-content: center;
          margin: 4px 0 14px;
        }

        .ch-training-board-header-inner {
          width: min(100%, 740px);
          display: flex;
          align-items: stretch;
          justify-content: space-between;
          gap: 16px;
        }

        .ch-training-board-house-row {
          display: flex;
          justify-content: center;
          margin: -4px 0 12px;
        }

        .ch-training-board-house-actions {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 10px;
          margin: -2px 0 14px;
        }

        .ch-training-board-house-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 36px;
          padding: 8px 14px;
          border-radius: 999px;
          background: rgba(255, 214, 97, 0.14);
          border: 1px solid rgba(255, 214, 97, 0.34);
          color: #ffe8a1;
          font-size: 0.82rem;
          font-weight: 900;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }

        .ch-training-board-house-action {
          min-height: 38px;
          padding: 8px 14px;
          border: 0;
          border-radius: 999px;
          font-size: 0.82rem;
          font-weight: 900;
        }

        .ch-training-board-house-action-map {
          background: rgba(255,255,255,0.12);
          color: #f5f7fb;
        }

        .ch-training-board-house-action-enter {
          background: linear-gradient(180deg, #f0d45d, #dea522);
          color: #241600;
        }

        .ch-training-board-stat {
          min-width: 180px;
          min-height: 78px;
          padding: 12px 18px;
          border-radius: 10px;
          background:
            linear-gradient(180deg, rgba(10, 12, 12, 0.96), rgba(3, 4, 4, 0.99));
          border: 1px solid rgba(255,255,255,0.12);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.08),
            inset 0 -1px 0 rgba(255,255,255,0.06),
            0 0 22px rgba(0,0,0,0.42),
            0 12px 20px rgba(0,0,0,0.2);
          color: #f5fff8;
          display: grid;
          align-content: center;
          gap: 6px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .ch-training-board-stat::before,
        .ch-training-board-stat::after {
          content: "";
          position: absolute;
          left: 12px;
          right: 12px;
          height: 2px;
          border-radius: 999px;
          pointer-events: none;
        }

        .ch-training-board-stat::before {
          top: 10px;
        }

        .ch-training-board-stat::after {
          bottom: 10px;
        }

        .ch-training-board-stat-user {
          border-color: rgba(92, 255, 166, 0.42);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.08),
            inset 0 -1px 0 rgba(255,255,255,0.06),
            0 0 26px rgba(52, 255, 142, 0.16),
            0 12px 20px rgba(0,0,0,0.22);
        }

        .ch-training-board-stat-user::before,
        .ch-training-board-stat-user::after {
          background: linear-gradient(90deg, transparent, rgba(78, 255, 157, 0.88), transparent);
          box-shadow:
            0 0 8px rgba(78, 255, 157, 0.92),
            0 0 16px rgba(52, 201, 118, 0.6);
        }

        .ch-training-board-stat-puzzle {
          border-color: rgba(255, 110, 110, 0.42);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.08),
            inset 0 -1px 0 rgba(255,255,255,0.06),
            0 0 26px rgba(255, 70, 70, 0.16),
            0 12px 20px rgba(0,0,0,0.22);
        }

        .ch-training-board-stat-puzzle::before,
        .ch-training-board-stat-puzzle::after {
          background: linear-gradient(90deg, transparent, rgba(255, 76, 76, 0.88), transparent);
          box-shadow:
            0 0 8px rgba(255, 86, 86, 0.94),
            0 0 16px rgba(224, 52, 52, 0.62);
        }

        .ch-training-board-stat .ch-training-toolbar-label {
          font-family: "Trebuchet MS", "Arial Narrow", Arial, sans-serif;
          font-size: 0.76rem;
          letter-spacing: 0.18em;
          text-transform: uppercase;
        }

        .ch-training-board-stat .ch-training-toolbar-value {
          font-family: "Courier New", "Lucida Console", Monaco, monospace;
          font-size: 1.72rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          line-height: 1;
          color: #ffffff;
        }

        .ch-training-board-stat-user .ch-training-toolbar-label,
        .ch-training-board-stat-user .ch-training-toolbar-value {
          color: #6cff9f;
          text-shadow:
            0 0 6px rgba(108, 255, 159, 1),
            0 0 14px rgba(75, 255, 142, 0.88),
            0 0 28px rgba(37, 182, 93, 0.72);
        }

        .ch-training-board-stat-puzzle .ch-training-toolbar-label,
        .ch-training-board-stat-puzzle .ch-training-toolbar-value {
          color: #ff5b5b;
          text-shadow:
            0 0 6px rgba(255, 91, 91, 1),
            0 0 14px rgba(255, 72, 72, 0.9),
            0 0 28px rgba(201, 39, 39, 0.72);
        }

        .ch-training-board-wrap {
          width: min(100%, 700px);
          margin: 0 auto;
          padding: 10px 10px 26px 24px;
          position: relative;
          z-index: 2;
          border-radius: 4px;
          background: rgba(5, 13, 18, 0.82);
          border: 2px solid rgba(71, 194, 155, 0.44);
          box-shadow:
            0 0 24px rgba(39, 190, 157, 0.16),
            0 10px 22px rgba(0,0,0,0.28);
        }

        .ch-training-board-shell {
          border-radius: 0;
        }

        .ch-training-toolbar {
          position: relative;
          width: min(100%, 810px);
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 8px;
          align-items: stretch;
          z-index: 3;
        }

        .ch-training-toolbar-card,
        .ch-training-toolbar-button {
          min-height: 90px;
          padding: 12px 10px;
          border-radius: 14px;
          background:
            linear-gradient(180deg, rgba(31, 73, 116, 0.98), rgba(9, 24, 48, 0.99));
          border: 1px solid rgba(132, 205, 255, 0.34);
          box-shadow:
            inset 0 0 0 1px rgba(255,255,255,0.07),
            0 0 18px rgba(66, 149, 255, 0.16),
            0 12px 20px rgba(0,0,0,0.2);
          color: #dbe8ff;
          text-align: center;
        }

        .ch-training-toolbar-card {
          display: grid;
          align-content: center;
          gap: 8px;
        }

        .ch-training-toolbar-button {
          display: grid;
          place-items: center;
          border: 0;
          font-weight: 900;
          cursor: pointer;
        }

        .ch-training-toolbar-button:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }

        .ch-training-toolbar-label {
          font-size: 0.78rem;
          font-weight: 900;
          letter-spacing: 0.02em;
          color: #eef5ff;
          text-transform: uppercase;
          line-height: 1.15;
        }

        .ch-training-toolbar-value {
          font-size: 1rem;
          font-weight: 800;
          color: #9ab8df;
          line-height: 1;
        }

        .ch-training-toolbar-button.ch-training-toolbar-button-active,
        .ch-training-toolbar-button:not(:disabled):hover {
          background:
            linear-gradient(180deg, rgba(58, 112, 184, 0.98), rgba(16, 44, 86, 0.99));
          color: #ffffff;
        }

        .ch-training-toolbar-button-description {
          background:
            linear-gradient(180deg, rgba(39, 113, 223, 0.98), rgba(13, 45, 112, 0.99));
          border: 1px solid rgba(125, 198, 255, 0.5);
          box-shadow:
            inset 0 0 0 1px rgba(255,255,255,0.08),
            0 0 24px rgba(54, 138, 255, 0.28),
            0 12px 20px rgba(0,0,0,0.2);
        }

        .ch-training-toolbar-button-description .ch-training-toolbar-label {
          color: #dceeff;
          text-shadow:
            0 0 6px rgba(106, 186, 255, 0.7),
            0 0 14px rgba(67, 149, 255, 0.32);
        }

        .ch-training-toolbar-button-next:not(:disabled) {
          background:
            linear-gradient(180deg, rgba(22, 172, 96, 0.98), rgba(8, 94, 52, 0.99));
          border: 1px solid rgba(118, 255, 180, 0.48);
          box-shadow:
            inset 0 0 0 1px rgba(255,255,255,0.08),
            0 0 24px rgba(32, 219, 123, 0.28),
            0 12px 20px rgba(0,0,0,0.2);
        }

        .ch-training-toolbar-button-hint {
          background:
            linear-gradient(180deg, rgba(173, 123, 14, 0.98), rgba(105, 69, 6, 0.99));
          border: 1px solid rgba(255, 221, 103, 0.5);
          box-shadow:
            inset 0 0 0 1px rgba(255,255,255,0.08),
            0 0 24px rgba(255, 196, 62, 0.26),
            0 12px 20px rgba(0,0,0,0.2);
        }

        .ch-training-toolbar-button-hint .ch-training-toolbar-label {
          color: #fff6d1;
          text-shadow:
            0 0 8px rgba(255, 230, 144, 0.88),
            0 0 18px rgba(255, 182, 60, 0.3);
        }

        .ch-training-toolbar-button-content {
          display: grid;
          justify-items: center;
          gap: 8px;
        }

        .ch-training-toolbar-button-icon {
          font-size: 1.1rem;
          opacity: 0.92;
        }

        .ch-training-toolbar-button-icon-description {
          color: #b9e4ff;
          text-shadow:
            0 0 10px rgba(116, 196, 255, 1),
            0 0 22px rgba(66, 149, 255, 0.58);
        }

        .ch-training-toolbar-button-icon-next {
          font-size: 1.42rem;
          color: #9affb8;
          text-shadow:
            0 0 10px rgba(108, 255, 164, 1),
            0 0 22px rgba(58, 214, 125, 0.64),
            0 0 34px rgba(38, 166, 93, 0.34);
        }

        .ch-training-toolbar-button-icon-hint {
          font-size: 1.2rem;
          color: #ffe368;
          text-shadow:
            0 0 10px rgba(255, 225, 107, 1),
            0 0 22px rgba(255, 188, 43, 0.6);
        }

        .ch-training-description-note {
          margin: 0 0 12px auto;
          width: min(100%, 420px);
          padding: 12px 14px;
          border-radius: 16px;
          background: rgba(8, 15, 28, 0.82);
          border: 1px solid rgba(255,255,255,0.08);
          color: #f3e9d3;
          font-size: 0.9rem;
          line-height: 1.45;
          box-shadow: 0 16px 28px rgba(0,0,0,0.22);
        }

        .ch-training-description-note-description {
          background: rgba(8, 22, 42, 0.84);
          border-color: rgba(92, 166, 255, 0.24);
          color: #e5f2ff;
          box-shadow:
            0 0 18px rgba(47, 134, 255, 0.14),
            0 16px 28px rgba(0,0,0,0.22);
        }

        .ch-training-description-note-description .ch-training-description-note-title {
          color: #9fd0ff;
          text-shadow:
            0 0 6px rgba(100, 176, 255, 0.76),
            0 0 14px rgba(61, 137, 255, 0.32);
        }

        .ch-training-description-note-hint {
          background: rgba(8, 26, 21, 0.84);
          border-color: rgba(90, 214, 152, 0.24);
          color: #e7fff1;
          box-shadow:
            0 0 18px rgba(61, 204, 128, 0.14),
            0 16px 28px rgba(0,0,0,0.22);
        }

        .ch-training-description-note-hint .ch-training-description-note-title {
          color: #adffd4;
          text-shadow:
            0 0 6px rgba(83, 255, 172, 0.7),
            0 0 14px rgba(56, 208, 128, 0.32);
        }

        .ch-training-hint-preview {
          position: relative;
          width: min(100%, 650px);
          margin: 0 auto 12px;
          display: grid;
          grid-template-columns: 190px minmax(0, 1fr);
          gap: 16px;
          align-items: center;
          padding: 14px 60px 14px 16px;
          border-radius: 22px;
          background: rgba(8, 43, 31, 0.92);
          border: 1px solid rgba(111, 255, 183, 0.42);
          box-shadow:
            0 0 24px rgba(61, 204, 128, 0.24),
            0 16px 28px rgba(0,0,0,0.22);
        }

        .ch-training-hint-preview-board-wrap {
          width: 100%;
          padding: 6px;
          border-radius: 14px;
          background: rgba(4, 16, 12, 0.82);
          border: 1px solid rgba(113, 231, 178, 0.32);
          box-shadow:
            0 0 18px rgba(72, 220, 146, 0.2),
            0 10px 18px rgba(0,0,0,0.2);
        }

        .ch-training-hint-preview-board-shell {
          width: 100%;
          aspect-ratio: 1 / 1;
          background: #000;
        }

        .ch-training-hint-preview-board,
        .ch-training-hint-preview-board .cg-wrap,
        .ch-training-hint-preview-board .cg-container {
          width: 100%;
          height: 100%;
        }

        .ch-training-hint-preview-content {
          min-width: 0;
          display: grid;
          gap: 12px;
        }

        .ch-training-hint-preview-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .ch-training-hint-preview-title {
          font-size: 0.72rem;
          font-weight: 900;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #d2ffe7;
          text-shadow:
            0 0 8px rgba(83, 255, 172, 0.94),
            0 0 18px rgba(56, 208, 128, 0.46);
        }

        .ch-training-hint-preview-close {
          position: absolute;
          top: 12px;
          right: 12px;
          z-index: 4;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          padding: 0;
          border: 1px solid rgba(214, 255, 233, 0.34);
          border-radius: 999px;
          background: rgba(5, 21, 15, 0.78);
          color: #f7fff9;
          font-size: 1.45rem;
          font-weight: 900;
          line-height: 1;
          box-shadow:
            inset 0 0 0 1px rgba(255,255,255,0.08),
            0 0 18px rgba(66, 222, 141, 0.18),
            0 10px 18px rgba(0,0,0,0.22);
        }

        .ch-training-hint-preview-close:hover {
          background: rgba(9, 36, 26, 0.96);
        }

        .ch-training-hint-preview-copy {
          color: #e7fff1;
          font-size: 0.92rem;
          line-height: 1.5;
        }

        .ch-training-hint-preview-progress {
          color: #a8d8be;
          font-size: 0.82rem;
          font-weight: 700;
        }

        .ch-training-hint-preview-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        .ch-training-hint-preview-button {
          min-width: 110px;
          padding: 10px 14px;
          border: 0;
          border-radius: 12px;
          background:
            linear-gradient(180deg, rgba(16, 170, 101, 0.98), rgba(7, 86, 51, 0.99));
          color: #ebfff4;
          font-size: 0.88rem;
          font-weight: 900;
          box-shadow:
            inset 0 0 0 1px rgba(255,255,255,0.08),
            0 0 18px rgba(32, 219, 123, 0.24),
            0 10px 18px rgba(0,0,0,0.18);
        }

        .ch-training-hint-preview-button:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        .ch-training-hint-preview-button-secondary {
          background: rgba(255,255,255,0.08);
          box-shadow:
            inset 0 0 0 1px rgba(255,255,255,0.08),
            0 10px 18px rgba(0,0,0,0.18);
        }

        .ch-training-hint-preview-button-secondary:hover {
          background: rgba(255,255,255,0.14);
        }

        .ch-training-description-comic {
          position: absolute;
          left: 50%;
          bottom: 112px;
          transform: translateX(-50%);
          width: min(calc(100% - 48px), 860px);
          margin: 0;
          display: grid;
          grid-template-columns: 300px minmax(0, 1fr);
          align-items: end;
          justify-content: center;
          column-gap: 0;
          z-index: 16;
          pointer-events: none;
        }

        .ch-training-description-mascot-wrap {
          position: relative;
          width: 340px;
          margin-right: -44px;
          align-self: end;
          justify-self: end;
          flex: 0 0 auto;
          z-index: 2;
        }

        .ch-training-description-mascot {
          width: 100%;
          display: block;
          filter: drop-shadow(0 20px 28px rgba(0,0,0,0.34));
        }

        .ch-training-description-bubble {
          position: relative;
          align-self: start;
          justify-self: start;
          width: min(100%, 520px);
          max-height: min(52vh, 480px);
          max-height: min(52dvh, 480px);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          pointer-events: auto;
          margin-top: 14px;
          padding: 18px 20px;
          border-radius: 26px;
          background: #ffffff;
          border: 2px solid rgba(18, 18, 18, 0.9);
          color: #111111;
          font-size: 0.98rem;
          line-height: 1.72;
          z-index: 1;
          box-shadow:
            0 12px 24px rgba(0,0,0,0.16),
            0 18px 34px rgba(0,0,0,0.16);
        }

        .ch-training-description-tail {
          position: absolute;
          background: #ffffff;
          border: 2px solid rgba(18, 18, 18, 0.9);
          border-radius: 50%;
          z-index: 3;
        }

        .ch-training-description-tail-primary {
          left: 208px;
          top: 82px;
          width: 24px;
          height: 24px;
          box-shadow: 0 5px 10px rgba(0,0,0,0.08);
        }

        .ch-training-description-tail-secondary {
          left: 192px;
          top: 108px;
          width: 12px;
          height: 12px;
          box-shadow: 0 4px 8px rgba(0,0,0,0.07);
        }

        .ch-training-description-bubble .ch-training-description-note-title {
          flex: 0 0 auto;
          color: #111111;
          text-shadow: none;
        }

        .ch-training-description-main-copy {
          min-height: 0;
          overflow-y: auto;
          overflow-wrap: anywhere;
          overscroll-behavior-y: contain;
          touch-action: pan-y;
          -webkit-overflow-scrolling: touch;
          color: #111111;
          font-weight: 700;
          line-height: 1.72;
        }

        .ch-training-description-note-title {
          margin-bottom: 6px;
          font-size: 0.72rem;
          font-weight: 900;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #9ab8df;
        }

        .ch-training-topbar,
        .ch-training-bottom-sheet,
        .ch-training-plan-row,
        .ch-training-plan-subtext,
        .ch-training-copy-block,
        .ch-training-ratings,
        .ch-training-turn,
        .ch-training-progress,
        .ch-training-tabs,
        .ch-training-panel,
        .ch-training-actions,
        .ch-training-user,
        .ch-training-upsell {
          display: none;
        }

        @media (max-width: 900px) {
          .ch-training-page {
            padding: 22px 16px 16px;
          }

          .ch-training-board-header-inner {
            width: min(100%, 660px);
          }

          .ch-training-board-wrap {
            width: min(100%, 600px);
          }

          .ch-training-side-hood {
            left: 12px;
            bottom: 30px;
            width: 64px;
          }

          .ch-training-toolbar {
            width: min(100%, 700px);
          }

          .ch-training-description-comic {
            width: min(calc(100% - 32px), 760px);
            bottom: 112px;
            grid-template-columns: 220px minmax(0, 1fr);
          }

          .ch-training-description-mascot-wrap {
            width: 248px;
            margin-right: -32px;
          }

          .ch-training-description-bubble {
            width: min(100%, 430px);
            margin-top: 8px;
            padding: 16px 18px;
          }

          .ch-training-description-tail-primary {
            left: 152px;
            top: 60px;
            width: 20px;
            height: 20px;
          }

          .ch-training-description-tail-secondary {
            left: 138px;
            top: 80px;
            width: 10px;
            height: 10px;
          }

          .ch-training-board-stat,
          .ch-training-toolbar-card,
          .ch-training-toolbar-button {
            min-height: 78px;
            padding: 10px 8px;
          }

          .ch-training-toolbar-label {
            font-size: 0.68rem;
          }

          .ch-training-toolbar-value {
            font-size: 0.9rem;
          }
        }

        @media (max-width: 640px) {
          .ch-training-phone-screen {
            padding: 8px;
          }

          .ch-training-overlay {
            align-items: flex-start;
            padding-top: 12px;
          }

          .ch-training-page {
            min-height: calc(100dvh - 16px);
            padding: 18px 12px 12px;
            border-radius: 22px;
          }

          .ch-training-side-rail {
            left: 8px;
            top: 16px;
            gap: 8px;
          }

          .ch-training-rail-button {
            width: 44px;
            height: 44px;
            border-radius: 13px;
          }

          .ch-training-ad-close {
            display: none;
          }

          .ch-training-board-header {
            margin: 0 0 10px;
            justify-content: flex-end;
            padding: 4px 0 0 84px;
            box-sizing: border-box;
          }

          .ch-training-board-header-inner {
            width: min(100%, 356px);
            max-width: 100%;
            margin-left: 0;
            gap: 8px;
          }

          .ch-training-board-stat {
            flex: 1 1 0;
            min-width: 0;
            min-height: 64px;
            padding: 10px 7px;
          }

          .ch-training-board-wrap {
            width: min(100%, 356px);
            padding: 6px 6px 20px 20px;
          }

          .ch-training-side-hood {
            left: -2px;
            bottom: 24px;
            width: 48px;
          }

          .ch-training-board-shell .cg-wrap coords {
            font-size: 10px;
          }

          .ch-training-board-shell .cg-wrap coords.ranks {
            left: -15px;
            width: 12px;
          }

          .ch-training-board-shell .cg-wrap coords.files {
            bottom: -16px;
            height: 12px;
          }

          .ch-training-toolbar {
            width: 100%;
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }

          .ch-training-toolbar-button {
            min-height: 62px;
          }

          .ch-training-toolbar-label {
            font-size: 0.62rem;
          }

          .ch-training-description-note {
            width: 100%;
            margin-bottom: 10px;
          }

          .ch-training-hint-preview {
            width: 100%;
            grid-template-columns: 1fr;
            gap: 12px;
            padding: 12px 52px 12px 12px;
          }

          .ch-training-hint-preview-board-wrap {
            max-width: 210px;
            margin: 0 auto;
          }

          .ch-training-hint-preview-content {
            justify-items: center;
            text-align: center;
          }

          .ch-training-hint-preview-header {
            width: 100%;
          }

          .ch-training-hint-preview-actions {
            justify-content: center;
          }

          .ch-training-hint-preview-button {
            min-width: 96px;
            padding: 9px 12px;
          }

          .ch-training-board-status-loading {
            padding-top: 24px;
          }

          .ch-training-board-status-hood-wrap {
            padding-top: 20px;
          }

          .ch-training-board-status-hood {
            transform: translateY(-8px);
          }

          .ch-training-description-comic {
            width: min(calc(100% - 20px), 100%);
            bottom: 94px;
            grid-template-columns: 146px minmax(0, 1fr);
            column-gap: 2px;
            align-items: end;
          }

          .ch-training-description-mascot-wrap {
            width: 188px;
            margin-right: -26px;
          }

          .ch-training-description-bubble {
            width: min(100%, 320px);
            margin-top: 6px;
            padding: 14px 14px;
            font-size: 0.84rem;
            line-height: 1.58;
            border-radius: 18px;
          }

          .ch-training-description-tail-primary {
            left: 114px;
            top: 44px;
            width: 16px;
            height: 16px;
          }

          .ch-training-description-tail-secondary {
            left: 102px;
            top: 60px;
            width: 8px;
            height: 8px;
          }
        }
      `}</style>

      <div className="ch-training-fit-stage">
        <div className="ch-training-page" style={{
          backgroundImage: "linear-gradient(180deg, rgba(0,0,0,.43), rgba(0,0,0,.22) 28%, rgba(0,0,0,.40) 100%), url('/trainingtlo.png')",
          backgroundPosition: "center",
          backgroundSize: "cover",
        }}>
          <header className="ch-training-mobile-header">
            <Link className="ch-training-mobile-brand" to="/" aria-label="Chesshood home">
              <img src="/logo.png" alt="" />
              <span>Chesshood</span>
            </Link>
            <ResponsiveAppMenu />
          </header>

          <Promotion pgn={promoting} onPromotion={promotion} />
          <div role="status" className="ch-training-today">
            {puzzleQaMode !== "real" ? "🧪 QA " : ""}
            {puzzleTrialActive ? "♛ " + trialTrainingLabel : isPro ? "♛ Unlimited puzzle training" : "♟ Free today: " +
              Math.max(0, 7 - (guestMode ? guestUsedToday : (dailyAccess?.usedToday || 0))) +
              " / 7 puzzles remaining"}
          </div>

          <div className="ch-training-board-header">
            <div className="ch-training-board-header-inner">
              <div className="ch-training-board-stat ch-training-board-stat-user">
                <span className="ch-training-toolbar-label">User Rating</span>
                <span className="ch-training-toolbar-value">{userRating}</span>
              </div>
              <div className="ch-training-board-stat ch-training-board-stat-puzzle">
                <span className="ch-training-toolbar-label">Puzzle Rating</span>
                <span className="ch-training-toolbar-value">
                  {puzzleRating}
                </span>
              </div>
            </div>
          </div>

          {entryMapMode && trainingHouseLabel && (
            <>
              <div className="ch-training-board-house-row">
                <div className="ch-training-board-house-pill">
                  Rating house {trainingHouseLabel}
                </div>
              </div>
              <div className="ch-training-board-house-actions">
                <button
                  type="button"
                  className="ch-training-board-house-action ch-training-board-house-action-map"
                  onClick={returnToTrainingMap}
                >
                  Exit to map
                </button>
                {canEnterDifferentTrainingHouse && (
                  <button
                    type="button"
                    className="ch-training-board-house-action ch-training-board-house-action-enter"
                    onClick={() => enterTrainingHouse(recommendedTrainingHouse)}
                  >
                    Enter {recommendedTrainingHouseLabel}
                  </button>
                )}
              </div>
            </>
          )}

          <div className="ch-training-mobile-coach">
            <img src="/hood.png" alt="Hood, your chess trainer" />
            <button type="button" className="ch-training-mobile-bubble"
              onClick={toggleDescriptionPanel}
              aria-label="Hood explains this training puzzle"
            >
              <strong>Hi, I'm Hood!</strong>
              <span>{isLoadingPuzzle
                ? "I'm choosing the best puzzle for your current level…"
                : loadError ? "Let's find another position that suits your level."
                : "I chose this position and its theme for you. Just focus on solving it!"
              }</span>
            </button>
          </div>

          <div className="ch-training-board-stage">
            {!alertInfo.show && (
              <img
                src="/hood.png"
                alt="Hood guide"
                className="ch-training-side-hood"
              />
            )}
            <div className="ch-training-board-wrap">
              <div className="ch-training-board-shell">
                {isLoadingPuzzle ? (
                  <div className="ch-training-board-status ch-training-board-status-loading">
                    <div className="ch-training-board-status-hood-wrap">
                      <div className="ch-training-board-status-bubble">
                        {guestMode
                          ? "Hood is preparing your guest starter puzzle."
                          : entryMapMode && trainingHouseLabel
                            ? `Hood is preparing the ${trainingHouseLabel} puzzle.`
                            : "Hood is preparing your next puzzle."}
                      </div>
                      <img
                        src="/hood.png"
                        alt="Hood loading"
                        className="ch-training-board-status-hood"
                      />
                    </div>
                  </div>
                ) : loadError ? (
                  <div className="ch-training-board-status">
                    <div>{loadError}</div>
                    <div className="ch-training-board-status-actions">
                      <button
                        type="button"
                        className="ch-training-retry"
                        onClick={repeat}
                      >
                        Retry
                      </button>
                      {entryMapMode && (
                        <button
                          type="button"
                          className="ch-training-retry"
                          onClick={returnToTrainingMap}
                        >
                          Back to map
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <Chessground
                    contained
                    config={config}
                    className="ch-training-board"
                  />
                )}
              </div>
            </div>
          </div>

          {activePanel === "description" && (
            <div className="ch-training-description-comic">
              <div className="ch-training-description-mascot-wrap">
                <img
                  src="/hood.png"
                  alt="Chesshood pawn mascot"
                  className="ch-training-description-mascot"
                />
                <span className="ch-training-description-tail ch-training-description-tail-primary" />
                <span className="ch-training-description-tail ch-training-description-tail-secondary" />
              </div>
              <div className="ch-training-description-bubble">
                <div
                  id="training-description-title"
                  className="ch-training-description-note-title"
                >
                  {descriptionTitle}
                </div>
                <div
                  className="ch-training-description-main-copy"
                  role="region"
                  aria-labelledby="training-description-title"
                  tabIndex={0}
                >
                  {descriptionCopy}
                </div>
              </div>
            </div>
          )}

          {activePanel === "hint" && (
            <div className="ch-training-hint-preview">
              <button
                type="button"
                className="ch-training-hint-preview-close"
                onClick={closeHintPanel}
                aria-label="Close hint board"
                title="Close hint board"
              >
                ×
              </button>
              <div className="ch-training-hint-preview-board-wrap">
                <div className="ch-training-hint-preview-board-shell">
                  <Chessground
                    contained
                    config={hintPreviewConfig}
                    className="ch-training-hint-preview-board"
                  />
                </div>
              </div>

              <div className="ch-training-hint-preview-content">
                <div className="ch-training-hint-preview-header">
                  <div className="ch-training-hint-preview-title">Hint Board</div>
                </div>
                <div className="ch-training-hint-preview-copy">
                  {hintCopy ||
                    "Use the board to preview the solution move by move."}
                </div>
                <div className="ch-training-hint-preview-progress">
                  Step {Math.min(hintStep, hintMoves.length)} of{" "}
                  {hintMoves.length}
                </div>
                <div className="ch-training-hint-preview-actions">
                  <button
                    type="button"
                    className="ch-training-hint-preview-button"
                    onClick={() =>
                      setHintStep((currentStep) => Math.max(0, currentStep - 1))
                    }
                    disabled={hintStep === 0}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    className="ch-training-hint-preview-button"
                    onClick={() =>
                      setHintStep((currentStep) =>
                        Math.min(hintMoves.length, currentStep + 1),
                      )
                    }
                    disabled={hintStep >= hintMoves.length}
                  >
                    Next
                  </button>
                  <button
                    type="button"
                    className="ch-training-hint-preview-button ch-training-hint-preview-button-secondary"
                    onClick={closeHintPanel}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="ch-training-mobile-footer">
            <div className="ch-training-mobile-turn">
              {sideToMoveLabel} to move
            </div>
            <div className="ch-training-mobile-progress" role="progressbar"
              aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progressWidth)}
              aria-label="Current puzzle progress"
            >
              <div className="ch-training-mobile-progress-fill" style={{ width: `${progressWidth}%` }} />
            </div>
            <div className="ch-training-mobile-options">
              <button type="button" className="ch-training-mobile-option"
                onClick={toggleDescriptionPanel}
                aria-pressed={activePanel === "description"}>
                <FontAwesomeIcon icon={faCircleInfo} />
                Description
              </button>
              <button type="button" className="ch-training-mobile-option"
                onClick={hint} disabled={isLoadingPuzzle || Boolean(loadError)}
                aria-pressed={activePanel === "hint"}>
                <FontAwesomeIcon icon={faLightbulb} />
                Hint
              </button>
            </div>
            <div className="ch-training-mobile-action-row">
              <button type="button" className="ch-training-mobile-repeat" onClick={repeat}
                disabled={isLoadingPuzzle || Boolean(loadError)}>
                <FontAwesomeIcon icon={faRepeat} />
                Repeat
              </button>
              <button type="button" className="ch-training-mobile-next"
                onClick={nextPuzzle} disabled={!puzzleFinished}>
                <FontAwesomeIcon icon={faArrowRight} />
                {nextActionLabel}
              </button>
            </div>
            <button type="button" className="ch-training-mobile-share"
              onClick={shareTraining}>
              <FontAwesomeIcon icon={faShareNodes} />
              Share Chesshood
            </button>
            {shareNotice && <div className="ch-training-mobile-share-status" role="status">{shareNotice}</div>}
          </div>
        </div>

        {showTrainingOverlay && (
          <div className="ch-training-overlay" role="dialog" aria-modal="true">
            <div className="ch-training-overlay-stack">
              {alertInfo.show && (
                <div className="ch-training-popup-card ch-training-feedback-popup">
                  <CustomAlert
                    variant={alertInfo.variant}
                    message={alertInfo.message}
                    onClose={hideAlert}
                    onRetry={repeat}
                    onNext={nextPuzzle}
                    user={alertInfo.user}
                    addedOrRestRating={alertInfo.addedOrRestRating}
                    puzzleSuccessMessage={alertInfo.puzzleSuccessMessage}
                    success={alertInfo.success}
                    fen={alertInfo.fen}
                    after_pgn={alertInfo.after_pgn}
                    config={alertInfo.config}
                    trainedCategoryKey={selectedCategoryKey}
                    trainedCategoryLabel={categoryDisplayLabel || categoryLabel}
                  />
                </div>
              )}

              {showTrainingAdPopup && !isPro && (
                <div className="ch-training-popup-card ch-training-ad-popup">
                  <button
                    type="button"
                    className="ch-training-ad-close"
                    onClick={hideAdPopup}
                    aria-disabled={adCloseLocked}
                    aria-label="Close advertisement"
                    title={adCloseLabel}
                    data-close-label={adCloseLabel}
                  >
                    ×
                  </button>
                  <WebTrainingAdSlot
                    key={`training-ad-break-${openedAdBreakCount ?? solvedPuzzleCount}`}
                    isPro={isPro}
                    isDue={showTrainingAd}
                    puzzlesUntilNextAd={puzzlesUntilNextAd}
                    onRenderStateChange={setTrainingAdRenderState}
                  />
                  <div className="ch-training-ad-footer">
                    <button
                      type="button"
                      className="ch-training-ad-footer-button"
                      onClick={hideAdPopup}
                      aria-disabled={adCloseLocked}
                    >
                      {adCloseLabel}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


